import { COVERAGE_MOCK, getReportModulesByType, getInitialModuleBlocks } from './reportConfiguratorBlocks';
import { getModuleData } from './mockModuleData';
import {
  REPORT_TEMPLATE_DATA_SOURCES,
  REPORT_TEMPLATE_MODULE_SOURCES,
  REPORT_TEMPLATE_SCHEMA,
  REPORT_TEMPLATE_SCHEMA_VERSION,
} from './reportTemplateSchema';
import { createTemplatePlaceholders } from './reportTemplatePlaceholders';
import { mapFascicoloFields } from './reportTemplateMappings';
import {
  buildBuildingsTable,
  buildCropSummaryTable,
  buildEquipmentTable,
  buildGenericTable,
  buildLivestockTable,
  buildParcelsTable,
  buildStationCoverageTable,
  buildStationInventoryTable,
} from './reportTemplateTables';
import { getBrandReportDesigner } from '../config/brand';
import { resolveLanguageCode } from './locale';

export const TEMPLATE_VIEWS = {
  TEXT: 'text',
  JSON: 'json',
  LATEX: 'latex',
};

const resolveModuleSources = (moduleId, reportType) => {
  const sources = REPORT_TEMPLATE_MODULE_SOURCES[moduleId];
  if (Array.isArray(sources)) {
    return sources;
  }
  if (sources && typeof sources === 'object') {
    return sources[reportType] || sources.default || ['userInput'];
  }
  return ['userInput'];
};

const buildModuleSourceLabels = (moduleId, reportType, t) => {
  const sources = resolveModuleSources(moduleId, reportType);
  return sources.map((sourceId) => {
    const source = REPORT_TEMPLATE_DATA_SOURCES[sourceId];
    return source ? t(source.labelKey) : sourceId;
  });
};

const createSection = (section) => ({
  id: section.id,
  title: section.title,
  level: section.level || 1,
  blocks: section.blocks || [],
  children: section.children || [],
  includeInToc: section.includeInToc !== false,
  isInternal: Boolean(section.isInternal),
});

const flattenSections = (sections, acc = []) => {
  sections.forEach((section) => {
    acc.push(section);
    if (section.children && section.children.length > 0) {
      flattenSections(section.children, acc);
    }
  });
  return acc;
};

const filterExportSections = (sections = []) => (
  sections
    .filter(section => !section?.isInternal)
    .map(section => ({
      ...section,
      blocks: (section.blocks || []).filter(block => !block?.isInternal),
      children: filterExportSections(section.children || []),
    }))
);

const buildTocItems = (sections) => {
  const counters = [];
  const items = [];
  sections.forEach((section) => {
    if (!section.title || section.includeInToc === false) {
      return;
    }
    const level = section.level || 1;
    counters[level - 1] = (counters[level - 1] || 0) + 1;
    counters.length = level;
    const number = counters.join('.');
    items.push({
      id: section.id,
      label: section.title,
      level,
      number,
    });
  });
  return items;
};

const isEmptyValue = (value) => {
  if (value == null) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
};

const resolveValueOrPlaceholder = (value, placeholder) => (
  isEmptyValue(value) ? placeholder : value
);

const normalizeImageList = (images) => (
  Array.isArray(images)
    ? images
      .filter(Boolean)
      .map(image => ({
        url: image.url,
        alt: image.alt || '',
        caption: image.caption || image.alt || '',
      }))
      .filter(image => image.url || image.alt || image.caption)
    : []
);

const getFirstBlockImageUrl = (blocks = []) => {
  for (const block of blocks) {
    if (!Array.isArray(block?.imageList)) {
      continue;
    }
    const firstWithUrl = block.imageList.find(image => image?.url);
    if (firstWithUrl?.url) {
      return firstWithUrl.url;
    }
  }
  return null;
};

const getCoverBackgroundUrl = (reportType, locale) => {
  const moduleCandidates = [
    { reportType, moduleId: 'm0' },
    { reportType: 'relazione_agronomica_primo_anno', moduleId: 'm0' },
    { reportType, moduleId: 'm2' },
  ];
  for (const candidate of moduleCandidates) {
    try {
      const blocks = getInitialModuleBlocks(candidate.reportType, candidate.moduleId, locale) || [];
      const imageUrl = getFirstBlockImageUrl(blocks);
      if (imageUrl) {
        return imageUrl;
      }
    } catch (error) {
      // Ignore fallback resolution errors and continue.
    }
  }
  return null;
};

const resolveReportLanguageCode = (locale) => resolveLanguageCode(locale);
const isEnglishLocale = (locale) => resolveReportLanguageCode(locale) === 'en';
const isSpanishLocale = (locale) => resolveReportLanguageCode(locale) === 'es';
const resolveReportText = (locale, italian, english, spanish = '') => {
  const languageCode = resolveReportLanguageCode(locale);
  if (languageCode === 'en') {
    return english || italian;
  }
  if (languageCode === 'es') {
    return spanish || english || italian;
  }
  return italian;
};
const TEMPLATE_PLACEHOLDER_REGEX = /(\{\{[^}]+\}\}|\[(?:MANUAL|OPTIONAL|TABLE|MAP|IMAGE|CHART|CAPTION):\s*[^\]]+\])/i;

const hasTemplatePlaceholder = (value) => (
  typeof value === 'string' && TEMPLATE_PLACEHOLDER_REGEX.test(value)
);

const resolveSafeText = (value, fallback = '') => {
  if (value == null) {
    return fallback;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || hasTemplatePlaceholder(trimmed)) {
      return fallback;
    }
    return trimmed;
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : fallback;
  }
  return String(value);
};

const getNotAvailableLabel = (locale) => (
  resolveReportText(locale, 'n.d.', 'Not available', 'No disponible')
);

const buildFallbackTable = ({ title, locale, captionLabel, caption }) => {
  return {
    type: 'table',
    title,
    columns: [
      { key: 'item', label: resolveReportText(locale, 'Voce', 'Item', 'Artículo') },
      { key: 'value', label: resolveReportText(locale, 'Valore', 'Value', 'Valor') },
    ],
    rows: [
      {
        item: title,
        value: getNotAvailableLabel(locale),
      },
    ],
    captionLabel,
    caption,
    cellFallback: getNotAvailableLabel(locale),
  };
};

const mergeNarrativeDetails = (baseNarratives, detailNarratives) => (
  Object.fromEntries(
    Object.entries(baseNarratives).map(([key, value]) => {
      const detail = detailNarratives[key];
      if (!value || !detail) {
        return [key, value];
      }
      return [key, `${value}\n\n${detail}`];
    })
  )
);

const splitNarrativeParagraphs = (text) => (
  String(text || '')
    .split(/\n{2,}/)
    .map(item => item.trim())
    .filter(Boolean)
);

const buildComplianceSectionNarratives = ({
  locale,
  reportYear,
  reportType,
  businessName,
  cuaa,
  farmAreaHa,
  stationCount,
  cropNames,
  generatedDate,
}) => {
  const languageCode = resolveReportLanguageCode(locale);
  const isEnglish = languageCode === 'en';
  const isSpanish = languageCode === 'es';
  const cropsLabel = cropNames && cropNames.length > 0
    ? cropNames.join(', ')
    : (resolveReportText(locale, 'grano duro e olivo', 'durum wheat and olive trees', 'trigo duro y olivos'));
  const farmAreaLabel = farmAreaHa != null
    ? new Intl.NumberFormat(locale || 'it-IT', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(farmAreaHa)
    : (resolveReportText(locale, '7,1', '7.1', '7.1'));
  const annualNote = reportType === 'relazione_agronomica_annuale'
    ? resolveReportText(
      locale,
      "La relazione è focalizzata sull'andamento annuale delle performance e sulla coerenza con i benchmark.",
      'The report focuses on annual performance trends and benchmark consistency.',
      'El informe se centra en las tendencias anuales de desempeño y la coherencia con los benchmark.'
    )
    : resolveReportText(
      locale,
      "La relazione documenta la configurazione del primo anno, la messa in esercizio e le evidenze agronomiche di baseline.",
      'The report documents first-year setup, commissioning, and baseline agronomic evidence.',
      'El informe documenta la configuración del primer año, la puesta en marcha y la evidencia agronómica de línea base.'
    );

  if (isEnglish) {
    const baseNarratives = {
      premessa: `This agronomic report supports DM Agrivoltaico and GSE monitoring obligations for reference year ${reportYear}. ${annualNote}`,
      objectives: 'The objective is to prove continuity of agricultural activity under agrivoltaic conditions through traceable monitoring, technical interpretation, and alignment with agronomic management records.',
      'environmental-analysis': `The farm profile refers to ${businessName} (CUAA ${cuaa}), with a managed area of ${farmAreaLabel} ha. The environmental baseline combines fascicolo data, parcel inventory, and operational records to frame the agronomic interpretation.`,
      'piano-colturale': `The cropping plan is structured around productive continuity, irrigation efficiency, and compatibility with the agrivoltaic layout. Main crop clusters considered in this period: ${cropsLabel}.`,
      'plan-organization': 'Cultivation areas are organized by operational zones to preserve machinery access, maintenance lanes, and clear agronomic responsibility by parcel and crop cycle.',
      'plan-surfaces': 'Cultivable surfaces per lot are reported with cadastral references and effective hectares, allowing direct validation of cultivated area continuity over time.',
      'plan-crops': 'Crop description includes varieties, parcel distribution, and expected yields, with emphasis on consistency between planned operations and observed field evidence.',
      'plan-monitoring': `Monitoring activities integrate station telemetry and agronomic observations. The current network includes ${stationCount} active stations between rear-module and open-field zones.`,
      'plan-irrigation': 'Irrigation and conservation analysis compares seasonal demand, recorded interventions, and observed water-use dynamics to support efficient resource planning.',
      'plan-soil': 'Soil monitoring tracks moisture and fertility indicators to assess root-zone conditions and medium-term recovery under agrivoltaic operation.',
      'plan-microclimate': 'Microclimate analysis evaluates temperature, humidity, and irradiation patterns across monitored zones to explain crop behavior and irrigation needs.',
      'plan-rotation': 'Crop rotation and scheduling are defined to preserve soil structure, reduce phytosanitary pressure, and maintain productive continuity.',
      mechanization: 'Mechanization is assessed against row spacing, access corridors, and operational safety, ensuring routine field activities remain feasible.',
      'crop-succession': 'Crop succession planning aligns expected agronomic windows with available field resources and monitoring evidence.',
      'implementation-analysis': 'Implementation and management analysis verifies that agronomic operations remain coherent with the approved project setup and annual field execution.',
      'construction-compatibility': 'Construction-system compatibility confirms that support structures and field logistics do not prevent standard agronomic operations.',
      'construction-layout': 'Plant layout review confirms operability of cultivation lanes, turning areas, and service access in relation to plot geometry.',
      'construction-resources': 'Human resources compatibility confirms the farm workforce and contractor availability are adequate for planned annual activities.',
      'integrated-project': 'Integrated project analysis consolidates agronomic, environmental, and operational evidence to assess the overall project balance.',
      'integrated-environmental': 'Environmental impacts are assessed with reference to soil protection, water-use rationalization, and microclimate adaptation effects.',
      'integrated-social': 'Social impacts are considered in terms of labor continuity, local operational stability, and maintainability of agricultural workflows.',
      'integrated-technical': 'Technical review highlights how monitoring technology, station placement, and data quality support defensible agronomic conclusions.',
      'costs-plv': 'Cost and PLV interpretation relates agronomic outputs to benchmark references, supporting annual consistency checks and traceable justification of deviations.',
      'guidelines-compliance': 'Consistency checks verify alignment with applicable GSE/DM Agrivoltaico requirements, including monitoring continuity and documentary coherence.',
      conclusions: `Based on available evidence as of ${generatedDate}, the farm management and monitoring setup are coherent with the report scope and support annual compliance checks.`,
      'conclusions-attestation': 'The undersigned agronomist confirms that the report has been prepared according to available technical and agronomic records.',
    };
    const detailNarratives = {
      premessa: reportType === 'relazione_agronomica_annuale'
        ? 'It consolidates field logs, telemetry evidence, and benchmark checks to support annual review decisions and corrective planning.'
        : 'It consolidates commissioning evidence, field logs, and telemetry baselines to support first-year validation and subsequent monitoring cycles.',
      objectives: 'Verification is driven by measurable indicators, traceable data lineage, and explicit checks on consistency between planned and executed agronomic actions.',
      'environmental-analysis': 'The section highlights soil context, water availability, and operational constraints that influence crop decisions and seasonal risk exposure.',
      'piano-colturale': 'It also defines seasonal priorities, decision checkpoints, and adaptation criteria to respond quickly to climate or productivity anomalies.',
      'plan-organization': 'This zoning approach reduces interference between fieldwork and maintenance activities while improving task allocation across teams.',
      'plan-surfaces': 'Declared and effectively cultivated surfaces are reconciled to strengthen documentary controls and annual traceability.',
      'plan-crops': 'For each crop cluster, the narrative links technical rationale, expected resource demand, and achievable yield targets.',
      'plan-monitoring': 'Collected datasets are validated for completeness, temporal coherence, and comparability between rear-module and open-field zones.',
      'plan-irrigation': 'Joint interpretation of soil moisture, weather events, and irrigation timing helps identify concrete efficiency opportunities.',
      'plan-soil': 'These indicators guide fertilization, conservative tillage choices, and prevention measures against structural degradation.',
      'plan-microclimate': 'Observed differences are interpreted to explain impacts on thermal stress, evapotranspiration, and crop response.',
      'plan-rotation': 'The sequence is designed to protect soil balance, lower phytosanitary pressure, and preserve medium-term productivity.',
      mechanization: 'Turning radii, machine footprint, and field operability windows are reviewed to avoid bottlenecks during peak periods.',
      'crop-succession': 'The proposed sequence remains updatable based on monitored outcomes, labor availability, and equipment constraints.',
      'implementation-analysis': 'Assessment includes execution status, procedural consistency, and reliability of operational records used for compliance.',
      'construction-compatibility': 'Potential interference points, service accessibility, and safety conditions are reviewed at plot and corridor level.',
      'construction-layout': 'The layout is stress-tested against seasonal peaks, routine operations, and extraordinary maintenance needs.',
      'construction-resources': 'Resource planning covers required competencies, staffing continuity, and specialist support for critical activities.',
      'integrated-project': 'Combining agronomic, environmental, and technical evidence provides a single defensible interpretation of site performance.',
      'integrated-environmental': 'The evaluation also documents cumulative effects and mitigation practices already adopted in ordinary operations.',
      'integrated-social': 'The analysis considers workforce continuity, local operating stability, and practical sustainability of the farm workflow.',
      'integrated-technical': 'It documents measurement robustness in terms of station coverage, data quality, and repeatability of observed trends.',
      'costs-plv': 'The economic reading prioritizes deviations, likely root causes, and actionable interventions for subsequent reporting cycles.',
      'guidelines-compliance': 'Each requirement is mapped to traceable evidence and explicit document references to improve audit readiness.',
      conclusions: 'The closing synthesis highlights residual risk areas, recommended operational adjustments, and priorities for the next cycle.',
      'conclusions-attestation': 'The attestation is issued for regulatory compliance and professional traceability purposes.',
    };
    return mergeNarrativeDetails(baseNarratives, detailNarratives);
  }

  if (isSpanish) {
    const baseNarratives = {
      premessa: `Este informe agronómico respalda las obligaciones de seguimiento de DM Agrivoltaico y GSE para el año de referencia ${reportYear}. ${annualNote}`,
      objectives: 'El objetivo es demostrar la continuidad de la actividad agrícola en condiciones agrivoltaicas mediante seguimiento trazable, interpretación técnica y alineación con registros de gestión agronómica.',
      'environmental-analysis': `El perfil de finca se refiere a ${businessName} (CUAA ${cuaa}), con un área manejada de ${farmAreaLabel} ha. La línea de base ambiental combina datos del fascículo, inventario de parcelas y registros operativos para enmarcar la interpretación agronómica.`,
      'piano-colturale': `El plan de cultivo se estructura en torno a la continuidad productiva, la eficiencia del riego y la compatibilidad con el esquema agrivoltaico. Principales grupos de cultivos considerados en este período: ${cropsLabel}.`,
      'plan-organization': 'Las áreas de cultivo están organizadas por zonas operativas para preservar el acceso de maquinaria, carriles de mantenimiento y una responsabilidad agronómica clara por parcela y ciclo de cultivo.',
      'plan-surfaces': 'Las superficies cultivables por lote se reportan con referencias catastrales y hectáreas efectivas, permitiendo validar directamente la continuidad del área cultivada en el tiempo.',
      'plan-crops': 'La descripción del cultivo incluye variedades, distribución de parcelas y rendimientos esperados, con énfasis en la coherencia entre las operaciones planificadas y la evidencia observada en el campo.',
      'plan-monitoring': `Las actividades de monitoreo integran la telemetría de la estación y las observaciones agronómicas. La red actual incluye ${stationCount} estaciones activas entre las zonas del módulo trasero y de campo abierto.`,
      'plan-irrigation': 'El análisis de riego y conservación compara la demanda estacional, las intervenciones registradas y la dinámica observada del uso del agua para respaldar una planificación eficiente de los recursos.',
      'plan-soil': 'El monitoreo del suelo rastrea los indicadores de humedad y fertilidad para evaluar las condiciones de la zona de las raíces y la recuperación a mediano plazo bajo operación agrivoltaica.',
      'plan-microclimate': 'El análisis del microclima evalúa los patrones de temperatura, humedad y irradiación en las zonas monitoreadas para explicar el comportamiento de los cultivos y las necesidades de riego.',
      'plan-rotation': 'Se define la rotación y programación de cultivos para preservar la estructura del suelo, reducir la presión fitosanitaria y mantener la continuidad productiva.',
      mechanization: 'La mecanización se evalúa en función del espacio entre filas, los corredores de acceso y la seguridad operativa, garantizando que las actividades de campo de rutina sigan siendo factibles.',
      'crop-succession': 'La planificación de la sucesión de cultivos alinea las ventanas agronómicas esperadas con los recursos de campo disponibles y la evidencia de monitoreo.',
      'implementation-analysis': 'El análisis de implementación y gestión verifica que las operaciones agronómicas sigan siendo coherentes con la configuración del proyecto aprobado y la ejecución anual en campo.',
      'construction-compatibility': 'La compatibilidad del sistema de construcción confirma que las estructuras de soporte y la logística de campo no impiden las operaciones agronómicas estándar.',
      'construction-layout': 'La revisión del diseño de la planta confirma la operatividad de los carriles de cultivo, las áreas de giro y el acceso a los servicios en relación con la geometría de la parcela.',
      'construction-resources': 'La compatibilidad de los recursos humanos confirma que la mano de obra agrícola y la disponibilidad de los contratistas son adecuadas para las actividades anuales planificadas.',
      'integrated-project': 'El análisis integrado del proyecto consolida evidencia agronómica, ambiental y operativa para evaluar el equilibrio general del proyecto.',
      'integrated-environmental': 'Los impactos ambientales se evalúan con referencia a la protección del suelo, la racionalización del uso del agua y los efectos de adaptación al microclima.',
      'integrated-social': 'Los impactos sociales se consideran en términos de continuidad laboral, estabilidad operativa local y mantenibilidad de los flujos de trabajo agrícolas.',
      'integrated-technical': 'La revisión técnica destaca cómo la tecnología de monitoreo, la ubicación de las estaciones y la calidad de los datos respaldan conclusiones agronómicas defendibles.',
      'costs-plv': 'La interpretación de costos y PLV relaciona los productos agronómicos con las referencias de referencia, lo que respalda los controles anuales de coherencia y la justificación rastreable de las desviaciones.',
      'guidelines-compliance': 'Los controles de coherencia verifican la alineación con los requisitos aplicables de GSE/DM Agrivoltaico, incluida la continuidad del seguimiento y la coherencia documental.',
      conclusions: `Según la evidencia disponible a partir del ${generatedDate}, la configuración de gestión y monitoreo de la granja es coherente con el alcance del informe y respalda los controles de cumplimiento anuales.`,
      'conclusions-attestation': 'El abajo firmante agrónomo confirma que el informe ha sido elaborado de acuerdo con los registros técnicos y agronómicos disponibles.',
    };
    const detailNarratives = {
      premessa: reportType === 'relazione_agronomica_annuale'
        ? 'Consolida registros de campo, evidencia de telemetría y verificaciones de benchmark para respaldar decisiones de revisión anual y planificación de acciones correctivas.'
        : 'Consolida evidencia de puesta en marcha, registros de campo y líneas base de telemetría para respaldar la validación del primer año y los ciclos de monitoreo posteriores.',
      objectives: 'La verificación está impulsada por indicadores mensurables, un linaje de datos rastreable y controles explícitos de la coherencia entre las acciones agronómicas planificadas y ejecutadas.',
      'environmental-analysis': 'La sección destaca el contexto del suelo, la disponibilidad de agua y las limitaciones operativas que influyen en las decisiones sobre cultivos y la exposición a riesgos estacionales.',
      'piano-colturale': 'También define prioridades estacionales, puntos de control de decisiones y criterios de adaptación para responder rápidamente a anomalías climáticas o de productividad.',
      'plan-organization': 'Este enfoque de zonificación reduce la interferencia entre el trabajo de campo y las actividades de mantenimiento al tiempo que mejora la asignación de tareas entre los equipos.',
      'plan-surfaces': 'Se concilian las superficies declaradas y efectivamente cultivadas para reforzar los controles documentales y la trazabilidad anual.',
      'plan-crops': 'Para cada grupo de cultivos, la narrativa vincula la justificación técnica, la demanda esperada de recursos y los objetivos de rendimiento alcanzables.',
      'plan-monitoring': 'Los conjuntos de datos recopilados se validan para determinar su integridad, coherencia temporal y comparabilidad entre las zonas del módulo trasero y de campo abierto.',
      'plan-irrigation': 'La interpretación conjunta de la humedad del suelo, los fenómenos climáticos y el momento del riego ayuda a identificar oportunidades concretas de eficiencia.',
      'plan-soil': 'Estos indicadores guían la fertilización, las opciones de labranza conservadora y las medidas de prevención contra la degradación estructural.',
      'plan-microclimate': 'Las diferencias observadas se interpretan para explicar los impactos sobre el estrés térmico, la evapotranspiración y la respuesta de los cultivos.',
      'plan-rotation': 'La secuencia está diseñada para proteger el equilibrio del suelo, disminuir la presión fitosanitaria y preservar la productividad a mediano plazo.',
      mechanization: 'Se revisan los radios de giro, la huella de la máquina y las ventanas de operabilidad en el campo para evitar cuellos de botella durante los períodos pico.',
      'crop-succession': 'La secuencia propuesta sigue siendo actualizable en función de los resultados monitoreados, la disponibilidad de mano de obra y las limitaciones de equipo.',
      'implementation-analysis': 'La evaluación incluye el estado de ejecución, la coherencia de los procedimientos y la confiabilidad de los registros operativos utilizados para el cumplimiento.',
      'construction-compatibility': 'Se revisan posibles puntos de interferencia, accesibilidad al servicio y condiciones de seguridad a nivel de parcela y corredor.',
      'construction-layout': 'El diseño se somete a pruebas de estrés frente a picos estacionales, operaciones de rutina y necesidades extraordinarias de mantenimiento.',
      'construction-resources': 'La planificación de recursos cubre las competencias requeridas, la continuidad del personal y el apoyo especializado para actividades críticas.',
      'integrated-project': 'La combinación de evidencia agronómica, ambiental y técnica proporciona una interpretación única defendible del desempeño del sitio.',
      'integrated-environmental': 'La evaluación también documenta los efectos acumulativos y las prácticas de mitigación ya adoptadas en las operaciones ordinarias.',
      'integrated-social': 'El análisis considera la continuidad de la fuerza laboral, la estabilidad operativa local y la sostenibilidad práctica del flujo de trabajo agrícola.',
      'integrated-technical': 'Documenta la solidez de las mediciones en términos de cobertura de estaciones, calidad de los datos y repetibilidad de las tendencias observadas.',
      'costs-plv': 'La lectura económica prioriza las desviaciones, las probables causas fundamentales y las intervenciones viables para los ciclos de presentación de informes posteriores.',
      'guidelines-compliance': 'Cada requisito se asigna a evidencia rastreable y referencias documentales explícitas para mejorar la preparación para la auditoría.',
      conclusions: 'La síntesis final destaca áreas de riesgo residual, ajustes operativos recomendados y prioridades para el próximo ciclo.',
      'conclusions-attestation': 'La certificación se emite con fines de cumplimiento normativo y trazabilidad profesional.',
    };
    return mergeNarrativeDetails(baseNarratives, detailNarratives);
  }

  const baseNarratives = {
    premessa: `La presente relazione agronomica supporta gli adempimenti DM Agrivoltaico e GSE per l'anno di riferimento ${reportYear}. ${annualNote}`,
    objectives: "L'obiettivo è dimostrare la continuità dell'attività agricola in contesto agrivoltaico mediante monitoraggio tracciabile, interpretazione tecnica e coerenza con i registri gestionali aziendali.",
    'environmental-analysis': `Il profilo aziendale fa riferimento a ${businessName} (CUAA ${cuaa}), con una superficie gestita pari a ${farmAreaLabel} ha. L'inquadramento ambientale integra dati di fascicolo, inventario particelle e registrazioni operative a supporto dell'interpretazione agronomica.`,
    'piano-colturale': `Il piano colturale è impostato su continuità produttiva, efficienza irrigua e compatibilità con il layout agrivoltaico. Colture prevalenti considerate nel periodo: ${cropsLabel}.`,
    'plan-organization': "Le aree di coltivazione sono organizzate per zone operative, mantenendo accessi per mezzi, corridoi di manutenzione e responsabilità agronomica per particella e ciclo.",
    'plan-surfaces': "Le superfici coltivabili per lotto sono riportate con riferimenti catastali ed ettari effettivi, così da consentire la verifica diretta della continuità d'uso agricolo.",
    'plan-crops': 'La descrizione delle colture include varietà, distribuzione per particella e rese attese, con attenzione alla coerenza tra pianificazione e riscontri di campo.',
    'plan-monitoring': `Le attività di monitoraggio integrano telemetria stazioni e osservazioni agronomiche. La rete attuale comprende ${stationCount} stazioni attive tra aree retro-modulo e campo aperto.`,
    'plan-irrigation': "L'analisi irrigua e conservativa confronta fabbisogni stagionali, interventi registrati e dinamiche di consumo idrico osservate a supporto della pianificazione.",
    'plan-soil': 'Il monitoraggio del suolo segue indicatori di umidità e fertilità per valutare condizioni della zona radicale e recupero nel medio periodo in esercizio agrivoltaico.',
    'plan-microclimate': "L'analisi microclimatica valuta andamenti di temperatura, umidità e irradiazione tra le zone monitorate per spiegare risposta colturale e fabbisogno irriguo.",
    'plan-rotation': 'Avvicendamento e cronoprogramma colturale sono definiti per preservare struttura del suolo, ridurre pressione fitosanitaria e mantenere continuità produttiva.',
    mechanization: "La meccanizzazione è valutata rispetto a sesti, corridoi di accesso e sicurezza operativa, garantendo la piena eseguibilità delle attività ordinarie.",
    'crop-succession': 'La successione colturale è impostata in coerenza con finestre agronomiche, risorse disponibili e riscontri emersi dal monitoraggio.',
    'implementation-analysis': "L'analisi delle attività di realizzazione e gestione verifica che le pratiche agronomiche restino coerenti con l'assetto progettuale approvato e con l'esercizio annuale.",
    'construction-compatibility': 'La compatibilità dei sistemi costruttivi conferma che strutture di supporto e logistica di campo non impediscono le normali operazioni colturali.',
    'construction-layout': "La verifica del layout impianto conferma operabilità di corsie colturali, aree di manovra e accessi di servizio in rapporto alla geometria dei lotti.",
    'construction-resources': 'La compatibilità delle risorse umane conferma adeguatezza del personale aziendale e dei supporti esterni rispetto al piano operativo annuo.',
    'integrated-project': 'Il progetto integrato consolida evidenze agronomiche, ambientali e operative per una valutazione complessiva di equilibrio tecnico-produttivo.',
    'integrated-environmental': 'Le ricadute ambientali sono valutate rispetto a tutela del suolo, razionalizzazione dei consumi idrici e adattamento microclimatico.',
    'integrated-social': "Le ricadute sociali sono considerate in termini di continuità occupazionale, stabilità operativa locale e sostenibilità dell'organizzazione agricola.",
    'integrated-technical': 'La componente tecnica evidenzia come tecnologia di monitoraggio, posizionamento stazioni e qualità del dato supportino conclusioni agronomiche verificabili.',
    'costs-plv': "L'analisi costi/PLV mette in relazione risultati agronomici e benchmark di riferimento, supportando la verifica annuale e la motivazione degli scostamenti.",
    'guidelines-compliance': 'La verifica di coerenza conferma allineamento ai requisiti applicabili GSE/DM Agrivoltaico, inclusa continuità del monitoraggio e coerenza documentale.',
    conclusions: `Sulla base delle evidenze disponibili alla data ${generatedDate}, l'impostazione gestionale e di monitoraggio risulta coerente con lo scopo della relazione e con la verifica annuale di conformità.`,
    'conclusions-attestation': "Il tecnico agronomo asseveratore conferma la redazione della relazione in conformità ai dati tecnici e agronomici disponibili.",
  };
  const detailNarratives = {
    premessa: reportType === 'relazione_agronomica_annuale'
      ? 'Il documento consolida registri di campo, telemetria e verifiche benchmark per supportare riesami periodici e pianificazione delle azioni correttive.'
      : 'Il documento consolida evidenze di messa in esercizio, registri di campo e baseline telemetriche per validare il primo ciclo operativo.',
    objectives: 'La verifica è condotta con indicatori misurabili, tracciabilità del dato e controlli espliciti sulla coerenza tra attività pianificate ed eseguite.',
    'environmental-analysis': 'Sono richiamate condizioni pedologiche, disponibilità idrica e vincoli operativi che influenzano scelte colturali e profilo di rischio stagionale.',
    'piano-colturale': 'Il piano definisce priorità stagionali, punti di controllo e criteri di adattamento in caso di anomalie climatiche o produttive.',
    'plan-organization': 'La segmentazione per zone riduce interferenze tra lavorazioni agricole e attività manutentive, migliorando la programmazione operativa.',
    'plan-surfaces': "Il confronto tra superfici dichiarate e superfici effettivamente coltivate è mantenuto tracciabile a supporto dei controlli documentali.",
    'plan-crops': 'Per ciascun gruppo colturale sono esplicitati razionale tecnico, fabbisogni principali e obiettivi di resa coerenti con il contesto aziendale.',
    'plan-monitoring': 'I dataset raccolti sono validati per completezza, coerenza temporale e confrontabilità tra aree retro-modulo e campo aperto.',
    'plan-irrigation': "La lettura congiunta di umidità del suolo, eventi meteo e turni irrigui consente di stimare margini concreti di efficientamento.",
    'plan-soil': 'Gli indicatori supportano decisioni su fertilizzazione, lavorazioni conservative e prevenzione del degrado strutturale del suolo.',
    'plan-microclimate': 'Le differenze osservate sono interpretate per valutare effetti su stress termico, evapotraspirazione e risposta colturale.',
    'plan-rotation': 'La sequenza proposta privilegia equilibrio nutrizionale, contenimento delle infestanti e resilienza produttiva nel medio periodo.',
    mechanization: 'Sono valutati raggi di volta, ingombri delle attrezzature e finestre operative per ridurre colli di bottiglia nelle fasi di picco.',
    'crop-succession': 'L\'impostazione resta aggiornabile in funzione dei risultati monitorati, della disponibilità di mezzi e della manodopera.',
    'implementation-analysis': "La valutazione include stato di avanzamento attività, conformità procedurale e affidabilità delle registrazioni operative.",
    'construction-compatibility': 'Sono analizzati punti di interferenza, accessibilità dei servizi e condizioni di sicurezza lungo i corridoi di lavoro.',
    'construction-layout': 'Il layout è verificato rispetto a stagionalità, operazioni ordinarie e necessità di manutenzione straordinaria.',
    'construction-resources': 'La pianificazione risorse considera competenze richieste, continuità delle squadre e supporti specialistici.',
    'integrated-project': 'L\'integrazione tra evidenze agronomiche, ambientali e tecniche permette una lettura unitaria delle prestazioni di sito.',
    'integrated-environmental': 'La valutazione considera anche effetti cumulativi e misure di mitigazione già applicate nella gestione ordinaria.',
    'integrated-social': 'L\'analisi include continuità delle attività locali, stabilità organizzativa e sostenibilità della filiera aziendale.',
    'integrated-technical': 'È documentata la robustezza del sistema di misura in termini di copertura, qualità e ripetibilità del dato.',
    'costs-plv': 'La lettura economica evidenzia trend, scostamenti dai target e priorità di intervento per il ciclo successivo.',
    'guidelines-compliance': 'Ogni requisito è ricondotto a evidenze verificabili e riferimenti documentali disponibili per audit e controlli.',
    conclusions: 'La sintesi finale evidenzia aree di attenzione residue, adeguamenti operativi suggeriti e priorità per il periodo successivo.',
    'conclusions-attestation': 'La presente attestazione è resa ai fini della conformità regolatoria e della tracciabilità tecnico-professionale.',
  };
  return mergeNarrativeDetails(baseNarratives, detailNarratives);
};

const formatShortDate = (value, locale) => {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat(locale || 'it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

const getLocalizedBlockText = (block, locale, key = 'bodyText') => {
  if (!block) {
    return '';
  }
  const spanishKey = `${key}Es`;
  const englishKey = `${key}En`;
  if (isSpanishLocale(locale) && block[spanishKey]) {
    return block[spanishKey];
  }
  if (isEnglishLocale(locale) && block[englishKey]) {
    return block[englishKey];
  }
  if (!isEnglishLocale(locale) && !isSpanishLocale(locale) && block[key]) {
    return block[key];
  }
  if (block[englishKey]) {
    return block[englishKey];
  }
  return block[key] || '';
};

const resolveImageCaption = (images) => {
  const normalized = normalizeImageList(images);
  const candidate = normalized.find(image => image.caption || image.alt);
  return candidate?.caption || candidate?.alt || '';
};

const buildNarrativeFromTextBlocks = (blocks) => {
  if (!Array.isArray(blocks)) {
    return '';
  }
  return blocks
    .map((block) => {
      const title = block.title?.trim();
      const body = block.bodyText?.trim();
      if (title && body) {
        return `${title}\n${body}`;
      }
      return body || title || '';
    })
    .filter(Boolean)
    .join('\n\n');
};

const findModuleTableBlock = (blocks, matcher) => {
  if (!Array.isArray(blocks)) {
    return null;
  }
  return blocks.find(block => block.type === 'table' && (!matcher || matcher(block)));
};

const serializeTable = (block, placeholder) => {
  const lines = [];
  const columns = block.columns || [];
  const rows = block.rows || [];
  lines.push(`[TABLE: ${block.title}]`);
  if (block.caption) {
    lines.push(`${block.captionLabel || 'Caption'}: ${block.caption}`);
  }
  if (columns.length === 0) {
    return lines;
  }
  lines.push(`| ${columns.map(col => col.label).join(' | ')} |`);
  lines.push(`| ${columns.map(() => '---').join(' | ')} |`);
  rows.forEach((row) => {
    const rowValues = columns.map((col) => {
      const value = row[col.key];
      if (value == null || value === '') {
        return block.cellFallback || placeholder;
      }
      return value;
    });
    lines.push(`| ${rowValues.join(' | ')} |`);
  });
  return lines;
};

const LATEX_ESCAPE_MAP = {
  '\\': '\\textbackslash{}',
  '{': '\\{',
  '}': '\\}',
  '$': '\\$',
  '&': '\\&',
  '#': '\\#',
  '_': '\\_',
  '%': '\\%',
  '~': '\\textasciitilde{}',
  '^': '\\textasciicircum{}',
};
const LATEX_PLACEHOLDER_REGEX = /(\{\{[^}]+\}\}|\[([A-Z]+):\s*([^\]]+)\])/g;
const LATEX_LINE_BREAK = '\\newline ';

const escapeLatex = (value) => {
  if (value == null) {
    return '';
  }
  const text = String(value);
  return text.replace(/[\\{}$&#_%~^]/g, (char) => LATEX_ESCAPE_MAP[char] || char);
};

const formatLatexPlaceholder = (label) => (
  `\\textit{\\textcolor{AgriGray600}{${escapeLatex(label)}}}`
);

const formatLatexWithPlaceholders = (value) => {
  if (value == null) {
    return '';
  }
  const text = String(value);
  let result = '';
  let lastIndex = 0;
  LATEX_PLACEHOLDER_REGEX.lastIndex = 0;
  let match = LATEX_PLACEHOLDER_REGEX.exec(text);
  while (match) {
    const [token, placeholderType, placeholderLabel] = match;
    if (match.index > lastIndex) {
      result += escapeLatex(text.slice(lastIndex, match.index));
    }
    if (token.startsWith('{{')) {
      const label = token.slice(2, -2).trim();
      result += formatLatexPlaceholder(label || token);
    } else {
      result += formatLatexPlaceholder(placeholderLabel || placeholderType || token);
    }
    lastIndex = match.index + token.length;
    match = LATEX_PLACEHOLDER_REGEX.exec(text);
  }
  if (lastIndex < text.length) {
    result += escapeLatex(text.slice(lastIndex));
  }
  return result;
};

const formatLatexText = (value) => (
  formatLatexWithPlaceholders(String(value ?? '')).replace(/\r?\n/g, LATEX_LINE_BREAK)
);

const getLatexSectionCommand = (level = 1, includeInToc = true) => {
  if (level <= 1) {
    return includeInToc ? '\\section' : '\\section*';
  }
  if (level === 2) {
    return includeInToc ? '\\subsection' : '\\subsection*';
  }
  if (level === 3) {
    return includeInToc ? '\\subsubsection' : '\\subsubsection*';
  }
  return includeInToc ? '\\paragraph' : '\\paragraph*';
};

const wrapLatexFilePath = (value) => `\\detokenize{${value}}`;

const buildLatexPlaceholderBox = (label, width = '0.9\\textwidth') => (
  `\\fcolorbox{AgriPrimary}{AgriPrimaryLight}{` +
  `\\parbox[c][0.22\\textheight][c]{${width}}{\\centering ` +
  `${formatLatexPlaceholder(label || 'Placeholder')}}}`
);

const buildLatexImageCandidates = (image) => {
  if (!image?.url) {
    return [];
  }
  const cleanedUrl = String(image.url).split('?')[0].split('#')[0];
  const baseName = cleanedUrl.split('/').pop();
  const candidates = [
    cleanedUrl,
    baseName,
    baseName ? `frontend/src/assets/${baseName}` : null,
    baseName ? `frontend/public/${baseName}` : null,
  ].filter(Boolean);
  return Array.from(new Set(candidates));
};

const buildLatexImageInclude = (image, width = '0.9\\textwidth', fallbackLabel = 'Image') => {
  const candidates = buildLatexImageCandidates(image);
  if (candidates.length === 0) {
    return buildLatexPlaceholderBox(fallbackLabel, width);
  }
  const fallback = buildLatexPlaceholderBox(fallbackLabel, width);
  return candidates.reduceRight((acc, candidate) => (
    `\\IfFileExists{${wrapLatexFilePath(candidate)}}{` +
    `\\includegraphics[width=${width}]{${wrapLatexFilePath(candidate)}}` +
    `}{${acc}}`
  ), fallback);
};

const serializeLatexTable = (block, placeholder) => {
  const columns = block.columns || [];
  const rows = block.rows || [];
  if (columns.length === 0) {
    return '';
  }
  const width = (0.95 / columns.length).toFixed(2);
  const columnSpec = columns.map(() => `p{${width}\\textwidth}`).join('');
  const headerRow = columns.map(col => formatLatexText(col.label || '')).join(' & ');
  const lines = [
    '\\begin{longtable}{' + columnSpec + '}',
  ];
  if (block.title) {
    lines.push(`\\caption{${formatLatexText(block.title)}}\\\\`);
  }
  lines.push('\\toprule');
  lines.push(`${headerRow} \\\\`);
  lines.push('\\midrule');
  lines.push('\\endfirsthead');
  lines.push('\\toprule');
  lines.push(`${headerRow} \\\\`);
  lines.push('\\midrule');
  lines.push('\\endhead');
  rows.forEach((row) => {
    const rowValues = columns.map((col) => {
      const value = row?.[col.key];
      const resolved = value != null && value !== '' ? value : block.cellFallback || placeholder;
      return formatLatexText(resolved);
    });
    lines.push(`${rowValues.join(' & ')} \\\\`);
  });
  lines.push('\\bottomrule');
  lines.push('\\end{longtable}');
  if (block.caption) {
    const label = block.captionLabel || 'Caption';
    lines.push(`\\textit{${formatLatexText(label)}: ${formatLatexText(block.caption)}}\\par`);
  }
  return lines.join('\n');
};

const serializeLatexFigure = ({
  title,
  caption,
  captionLabel,
  imageList,
  placeholderLabel,
  width,
}) => {
  const images = imageList || [];
  const imageWidth = width || (images.length > 1 ? '0.45\\textwidth' : '0.85\\textwidth');
  const titleText = title ? formatLatexText(title) : '';
  const captionText = caption
    ? `${captionLabel ? `${formatLatexText(captionLabel)}: ` : ''}${formatLatexText(caption)}`
    : '';
  const lines = ['\\begin{figure}[htbp]', '\\centering'];
  if (images.length === 0) {
    lines.push(buildLatexPlaceholderBox(placeholderLabel || titleText || 'Figure', imageWidth));
  } else {
    images.forEach((image) => {
      lines.push(buildLatexImageInclude(image, imageWidth, placeholderLabel || titleText || 'Figure'));
    });
  }
  if (captionText) {
    lines.push(`\\caption{${captionText}}`);
  } else if (titleText) {
    lines.push(`\\caption{${titleText}}`);
  }
  lines.push('\\end{figure}');
  return lines.join('\n');
};

const serializeLatexBlock = (block, placeholder) => {
  if (!block) {
    return '';
  }
  if (block.isInternal) {
    return '';
  }
  switch (block.type) {
    case 'cover':
      return '';
    case 'paragraph':
      return `${formatLatexText(block.text || '')}\\par`;
    case 'fieldList': {
      const items = block.items || [];
      if (items.length === 0) {
        return '';
      }
      const lines = ['\\begin{description}'];
      items.forEach((item) => {
        const label = formatLatexText(item.label || '');
        const value = formatLatexText(item.value || '');
        lines.push(`\\item[${label}] ${value}`);
      });
      lines.push('\\end{description}');
      return lines.join('\n');
    }
    case 'meta': {
      const label = formatLatexText(block.label || '');
      const value = formatLatexText(block.value || '');
      return `\\noindent\\textcolor{AgriGray600}{\\textbf{${label}}${value ? `: ${value}` : ''}}\\par`;
    }
    case 'toc':
      return '\\tableofcontents\\clearpage';
    case 'gapReport': {
      const items = block.items || [];
      if (items.length === 0) {
        return `${formatLatexText(block.emptyMessage || '')}\\par`;
      }
      const lines = ['\\begin{itemize}'];
      items.forEach((item) => {
        lines.push(`\\item ${formatLatexText(item.label || '')}`);
      });
      lines.push('\\end{itemize}');
      return lines.join('\n');
    }
    case 'table':
      return serializeLatexTable(block, placeholder);
    case 'map': {
      const zoneText = block.zoneLabels?.length
        ? block.zoneLabels.join(', ')
        : block.zonePlaceholder || '';
      const imageLabel = block.imageLabel || 'Image';
      const imagePlaceholder = block.imagePlaceholder || '';
      const lines = [
        serializeLatexFigure({
          title: block.title,
          caption: block.caption,
          captionLabel: block.captionLabel,
          imageList: block.imageList,
          placeholderLabel: block.title || 'Map',
        }),
      ];
      if (zoneText) {
        lines.push(`\\textbf{${formatLatexText(block.zoneLabelLabel || 'Zones')}}: ${formatLatexText(zoneText)}\\par`);
      }
      if (!block.imageList?.length) {
        lines.push(`\\textbf{${formatLatexText(imageLabel)}}: ${formatLatexText(imagePlaceholder)}\\par`);
      }
      return lines.join('\n');
    }
    case 'chart':
      return serializeLatexFigure({
        title: block.title,
        caption: block.caption,
        captionLabel: block.captionLabel,
        imageList: block.imageList,
        placeholderLabel: block.title || 'Chart',
      });
    case 'image': {
      const lines = [
        serializeLatexFigure({
          title: block.title,
          caption: block.caption,
          captionLabel: block.captionLabel,
          imageList: block.imageList,
          placeholderLabel: block.title || 'Image',
        }),
      ];
      const imageCaptions = (block.imageList || [])
        .map(image => image?.caption)
        .filter(Boolean);
      if (imageCaptions.length > 0) {
        lines.push('\\begin{itemize}');
        imageCaptions.forEach((caption) => {
          lines.push(`\\item ${formatLatexText(caption)}`);
        });
        lines.push('\\end{itemize}');
      }
      return lines.join('\n');
    }
    case 'placeholder':
      return `${buildLatexPlaceholderBox(block.text || 'Placeholder')}\\par`;
    default:
      return '';
  }
};

const serializeLatexSection = (section, placeholder) => {
  if (!section) {
    return '';
  }
  const lines = [];
  const isTocSection = section.id === 'toc';
  if (section.title && !isTocSection) {
    const command = getLatexSectionCommand(section.level || 1, section.includeInToc !== false);
    lines.push(`${command}{${formatLatexText(section.title)}}`);
  }
  (section.blocks || []).forEach((block) => {
    const blockContent = serializeLatexBlock(block, placeholder);
    if (blockContent) {
      lines.push(blockContent);
    }
  });
  (section.children || []).forEach((child) => {
    const childContent = serializeLatexSection(child, placeholder);
    if (childContent) {
      lines.push(childContent);
    }
  });
  return lines.join('\n');
};

const buildLatexCoverPage = ({
  coverLines,
  coverFields,
  reportTypeLabel,
}) => {
  const titleLine = coverLines?.[0] || reportTypeLabel || '';
  const subtitleLine = coverLines?.[1] || '';
  const generatedLine = coverLines?.[2] || '';
  const fieldRows = coverFields || [];
  const lines = [
    '\\begin{titlepage}',
    '\\pagecolor{AgriBackground}',
    '\\thispagestyle{empty}',
    '\\centering',
    '\\vspace*{1.5cm}',
    '\\vspace{1.8cm}',
    `{\\Huge\\bfseries\\color{AgriPrimary}${formatLatexText(titleLine)}}\\par`,
    subtitleLine ? `{\\Large\\color{AgriGray600}${formatLatexText(subtitleLine)}}\\par` : '',
    reportTypeLabel ? `\\vspace{0.5cm}{\\large\\textcolor{AgriGray600}{${formatLatexText(reportTypeLabel)}}}\\par` : '',
    '\\vspace{1.2cm}',
  ];
  if (fieldRows.length > 0) {
    lines.push('\\begin{tabularx}{0.9\\textwidth}{>{\\bfseries}lX}');
    fieldRows.forEach((field, index) => {
      const label = formatLatexText(field.label || '');
      const value = formatLatexText(field.value || '');
      const suffix = index === fieldRows.length - 1 ? '' : ' \\\\';
      lines.push(`${label} & ${value}${suffix}`);
    });
    lines.push('\\end{tabularx}');
  }
  lines.push('\\vfill');
  if (generatedLine) {
    lines.push(`{\\small\\color{AgriGray600}${formatLatexText(generatedLine)}}\\par`);
  }
  lines.push('\\end{titlepage}');
  lines.push('\\pagecolor{white}');
  return lines.filter(Boolean).join('\n');
};

const buildLatexAppendix = (locale) => {
  const appendixTitle = resolveReportText(locale, 'Appendici ed evidenze', 'Appendices and Evidence Binder', 'Carpeta de apéndices y pruebas');
  const packages = [
    {
      id: '01_domanda',
      description: resolveReportText(
        locale,
        'Domanda di partecipazione: DSAN, autorizzazioni, connessione, GAUDÌ, DNSH ex-ante.',
        'Application package: DSAN, permits, connection, GAUDÌ, DNSH ex-ante.',
        'Paquete de solicitud: DSAN, permisos, conexión, GAUDÌ, DNSH ex-ante.'
      ),
    },
    {
      id: '02_eoo',
      description: resolveReportText(
        locale,
        'Entrata in esercizio: relazione asseverata, allegati tecnici, PVGIS, foto targhette moduli, DNSH ex-post.',
        'Entry into operation: asseverated report, technical annexes, PVGIS, module photos, DNSH ex-post.',
        'Entrada en operación: informe certificado, anexos técnicos, PVGIS, fotos de placas de módulos, DNSH ex-post.'
      ),
    },
    {
      id: '03_monitoraggi_annuali/AAAA',
      description: resolveReportText(
        locale,
        'Monitoraggi annuali: relazione + allegati (RICA/fascicolo, PLV, misure acqua/suolo/microclima, audit).',
        'Annual monitoring: report + annexes (RICA/fascicolo, PLV, water/soil/microclimate measures, audit).',
        'Monitoreo anual: informe + anexos (RICA/fascículo, PLV, mediciones de agua/suelo/microclima, auditoría).'
      ),
    },
    {
      id: '04_pnrr',
      description: resolveReportText(
        locale,
        'Pacchetto PNRR: atto d’obbligo, avanzamento, rendicontazione/tracciabilità.',
        'PNRR package: obligations, progress reporting, financial traceability.',
        'Paquete PNRR: obligaciones, avance de ejecución y trazabilidad financiera.'
      ),
    },
    {
      id: '05_contratti_e_pagamenti',
      description: resolveReportText(
        locale,
        'Contratti e pagamenti: fatture, corrispettivi, conguagli, oneri istruttori.',
        'Contracts and payments: invoices, fees, reconciliations.',
        'Contratos y pagos: facturas, honorarios y conciliaciones.'
      ),
    },
    {
      id: '06_modifiche_e_comunicazioni',
      description: resolveReportText(
        locale,
        'Modifiche e comunicazioni: PEC, riscontri GSE, change log, DNSH aggiornato.',
        'Changes and communications: PEC, GSE feedback, change log, updated DNSH.',
        'Modificaciones y comunicaciones: PEC, respuestas de GSE, registro de cambios y DNSH actualizado.'
      ),
    },
    {
      id: '07_controlli_sopralluoghi',
      description: resolveReportText(
        locale,
        'Controlli e sopralluoghi: dossier, verbali, prescrizioni, prove attuazione.',
        'Inspections: dossiers, minutes, prescriptions, remediation evidence.',
        'Inspecciones y visitas: expedientes, actas, prescripciones y evidencias de implementación.'
      ),
    },
  ];
  const lines = [
    '\\appendix',
    `\\section{${escapeLatex(appendixTitle)}}`,
  ];
  packages.forEach((entry) => {
    lines.push(`\\subsection{${escapeLatex(entry.id)}}`);
    lines.push(`${formatLatexText(entry.description)}\\par`);
  });
  return lines.join('\n');
};

const buildLatexDocument = ({
  sections,
  placeholder,
  coverLines,
  coverFields,
  reportTypeLabel,
  reportTitleLabel,
  projectTitle,
  locale,
  tocTitle,
}) => {
  const tocName = tocTitle ? escapeLatex(tocTitle) : null;
  const projectLabel = resolveReportText(locale, 'PROGETTO AGROVOLTAICO', 'AGRIVOLTAIC PROJECT', 'PROYECTO AGRIVOLTAICO');
  const headerProjectText = projectTitle
    ? `${projectLabel} ${projectTitle}`
    : projectLabel;
  const headerReportText = reportTitleLabel ? reportTitleLabel : '';
  const pageLabel = resolveReportText(locale, 'Pag.', 'Page', 'Página');
  const pageOfLabel = resolveReportText(locale, 'di', 'of', 'de');
  const lines = [
    '\\documentclass[11pt,a4paper]{article}',
    '\\usepackage{iftex}',
    '\\ifPDFTeX',
    '\\usepackage[utf8]{inputenc}',
    '\\usepackage[T1]{fontenc}',
    '\\usepackage{lmodern}',
    '\\renewcommand{\\familydefault}{\\sfdefault}',
    '\\else',
    '\\usepackage{fontspec}',
    '\\IfFontExistsTF{Inter}{\\setmainfont{Inter}\\setsansfont{Inter}}{\\setmainfont{Latin Modern Sans}\\setsansfont{Latin Modern Sans}}',
    '\\fi',
    '\\usepackage{geometry}',
    '\\geometry{margin=2.4cm}',
    '\\usepackage{graphicx}',
    '\\graphicspath{{./}{./frontend/public/}{./frontend/src/assets/}}',
    '\\usepackage{longtable}',
    '\\usepackage{booktabs}',
    '\\usepackage{array}',
    '\\usepackage{tabularx}',
    '\\usepackage{enumitem}',
    '\\usepackage{xcolor}',
    '\\usepackage{titlesec}',
    '\\usepackage{hyperref}',
    '\\usepackage{fancyhdr}',
    '\\usepackage{float}',
    '\\usepackage{lastpage}',
    '\\definecolor{AgriPrimary}{HTML}{4A5A3A}',
    '\\definecolor{AgriPrimaryDark}{HTML}{3A4A2A}',
    '\\definecolor{AgriPrimaryLight}{HTML}{D4E5C7}',
    '\\definecolor{AgriGray900}{HTML}{1A1A1A}',
    '\\definecolor{AgriGray600}{HTML}{6B6B6B}',
    '\\definecolor{AgriBackground}{HTML}{F5F5F0}',
    '\\titleformat{\\section}{\\color{AgriPrimary}\\Large\\bfseries}{\\thesection}{1em}{}',
    '\\titleformat{\\subsection}{\\color{AgriPrimaryDark}\\large\\bfseries}{\\thesubsection}{1em}{}',
    '\\titleformat{\\subsubsection}{\\color{AgriGray900}\\normalsize\\bfseries}{\\thesubsubsection}{1em}{}',
    '\\setlist[description]{style=nextline,leftmargin=0pt,labelsep=1em,font=\\normalfont\\bfseries}',
    '\\setlength{\\parindent}{0pt}',
    '\\setlength{\\parskip}{0.6em}',
    '\\hypersetup{colorlinks=true,linkcolor=AgriPrimary,urlcolor=AgriPrimary}',
    resolveReportText(locale, '\\renewcommand{\\tablename}{Tabella}', '', ''),
    resolveReportText(locale, '\\renewcommand{\\figurename}{Figura}', '', ''),
    tocName ? `\\renewcommand{\\contentsname}{${tocName}}` : '',
    '\\pagestyle{fancy}',
    '\\fancyhf{}',
    `\\fancyhead[L]{\\textcolor{AgriGray600}{${escapeLatex(headerProjectText)}}}`,
    `\\fancyhead[R]{\\textcolor{AgriGray600}{${escapeLatex(headerReportText)}}}`,
    `\\fancyfoot[C]{\\textcolor{AgriGray600}{${pageLabel} \\thepage\\ ${pageOfLabel} \\pageref{LastPage}}}`,
    '\\begin{document}',
    buildLatexCoverPage({
      coverLines,
      coverFields,
      reportTypeLabel,
      locale,
    }),
  ];
  (sections || []).forEach((section) => {
    if (section.id === 'cover') {
      return;
    }
    const content = serializeLatexSection(section, placeholder);
    if (content) {
      lines.push(content);
    }
  });
  lines.push(buildLatexAppendix(locale));
  lines.push('\\end{document}');
  return lines.join('\n');
};

const serializeBlock = (block, placeholder) => {
  if (!block) {
    return [];
  }
  if (block.isInternal) {
    return [];
  }
  switch (block.type) {
    case 'cover':
      return block.lines || [];
    case 'paragraph':
      return [block.text];
    case 'fieldList':
      return block.items.map(item => `- ${item.label}: ${item.value}`);
    case 'meta':
      return [`${block.label}: ${block.value}`];
    case 'toc':
      return block.items.map((item) => {
        const indent = '  '.repeat(Math.max(0, item.level - 1));
        return `${indent}${item.number} ${item.label}`;
      });
    case 'gapReport':
      if (!block.items.length) {
        return [block.emptyMessage];
      }
      return block.items.map(item => `- ${item.label}`);
    case 'table':
      return serializeTable(block, placeholder);
    case 'map':
      return [
        `[MAP: ${block.title}]`,
        `${block.captionLabel}: ${block.caption}`,
        block.zoneLabels?.length
          ? `${block.zoneLabelLabel}: ${block.zoneLabels.join(', ')}`
          : `${block.zoneLabelLabel}: ${block.zonePlaceholder}`,
        `${block.imageLabel}: ${block.imagePlaceholder}`,
      ];
    case 'chart':
      return [
        `[CHART: ${block.title}]`,
        `${block.captionLabel}: ${block.caption}`,
      ];
    case 'image':
      return [
        `[IMAGE: ${block.title}]`,
        `${block.captionLabel}: ${block.caption}`,
      ];
    case 'placeholder':
      return [block.text];
    default:
      return [];
  }
};

const serializeSection = (section, placeholder) => {
  const lines = [];
  if (section.title) {
    const hashes = '#'.repeat(Math.min(Math.max(section.level || 1, 1), 6));
    lines.push(`${hashes} ${section.title}`);
  }
  section.blocks.forEach((block) => {
    const blockLines = serializeBlock(block, placeholder);
    if (blockLines.length > 0) {
      lines.push(...blockLines, '');
    }
  });
  section.children.forEach((child) => {
    lines.push(...serializeSection(child, placeholder), '');
  });
  return lines;
};

export const buildReportTemplateDocument = ({
  report,
  reportType,
  locale = 'it',
  t,
  dossier,
  stations,
}) => {
  const resolvedType = reportType || report?.type || 'relazione_agronomica_primo_anno';
  const schema = REPORT_TEMPLATE_SCHEMA[resolvedType] || REPORT_TEMPLATE_SCHEMA.relazione_agronomica_primo_anno;
  const placeholders = createTemplatePlaceholders(t);
  const isAnnualReport = resolvedType === 'relazione_agronomica_annuale';
  const isFirstYearReport = resolvedType === 'relazione_agronomica_primo_anno';
  const noDataLabel = getNotAvailableLabel(locale);
  const missingValuePlaceholder = noDataLabel;
  const resolveCaptionText = (value, fallbackText) =>
    resolveSafeText(value, fallbackText || noDataLabel);
  const resolveManualText = (value, fallbackText) =>
    resolveSafeText(value, fallbackText || '');
  const resolveImageCaptionText = (images, fallbackLabel) =>
    resolveCaptionText(resolveImageCaption(images), fallbackLabel);
  const resolveImagePlaceholder = (images, fallbackLabel) => {
    const normalized = normalizeImageList(images);
    const caption = resolveImageCaption(normalized);
    const value = caption || normalized[0]?.url || '';
    return resolveSafeText(value, fallbackLabel || noDataLabel);
  };
  const getBlockText = (block, key = 'bodyText') => getLocalizedBlockText(block, locale, key);
  const buildTextParagraphBlocks = (block, fallbackLabel) => {
    if (!block) {
      return [];
    }
    const resolvedText = resolveManualText(
      block.bodyText,
      block.bodyPlaceholderKey ? t(block.bodyPlaceholderKey) : fallbackLabel
    );
    const paragraphBlocks = [];
    if (block.title) {
      paragraphBlocks.push({ type: 'paragraph', text: block.title });
    }
    if (resolvedText) {
      paragraphBlocks.push({ type: 'paragraph', text: resolvedText });
    }
    return paragraphBlocks;
  };

  const reportTitle = resolveSafeText(report?.name, t('reports.workspace.fallback.report'));
  const reportYear = report?.year || new Date().getFullYear();
  const generatedAt = new Date().toISOString();
  const formattedGeneratedDate = formatShortDate(generatedAt, locale);
  const reportIdValue = resolveSafeText(report?.id, 'report-demo');
  const defaultDocumentCode = resolveReportText(
    locale,
    `AGR-REL-${reportYear}-${reportIdValue}`,
    `AGR-REP-${reportYear}-${reportIdValue}`,
    `AGR-INF-${reportYear}-${reportIdValue}`,
  );
  const reportDefaults = {
    region: resolveReportText(locale, 'Lazio', 'Lazio', 'Lacio'),
    province: 'RM',
    comune: resolveReportText(locale, 'Roma', 'Rome', 'Roma'),
    power: '1.2 MWp',
    storage: resolveReportText(locale, 'Sistema di accumulo 200 kWh', 'Battery system 200 kWh', 'Sistema de batería 200 kWh'),
    gridConnection: resolveReportText(locale, 'Codice di connessione RM-AV-102', 'Grid code RM-AV-102', 'Código de red RM-AV-102'),
    documentCode: defaultDocumentCode,
    revision: isAnnualReport ? 'ver 2' : 'ver 1',
    designer: getBrandReportDesigner(locale),
    agronomist: resolveReportText(locale, 'Dott. Agr. Marco Rossi', 'Dr. Agr. Marco Rossi', 'Dr. Agr. Marco Rossi'),
    confidentiality: resolveReportText(locale, 'Documento riservato ad uso tecnico e adempimenti di conformità.', 'Confidential document for technical and compliance use.', 'Documento confidencial para uso técnico y de cumplimiento.'),
  };
  const fallbackStations = Array.isArray(COVERAGE_MOCK?.stations)
    ? COVERAGE_MOCK.stations.map((station) => ({
      id: station.id,
      stationCode: station.name || station.id,
      stationType: station.type,
      sensorType: station.type,
      locationDescription: station.zoneId || station.id,
      sectionId: station.zoneId || null,
      status: 'active',
      coordinates: station.coordinates,
      type: station.type,
    }))
    : [];
  const effectiveStations = Array.isArray(stations) && stations.length > 0
    ? stations
    : fallbackStations;

  const mappedFascicolo = mapFascicoloFields({
    dossier,
    locale,
    t,
    placeholders,
  });
  const fascicoloFallbackValues = {
    cuaa: 'IT12345678901',
    legalForm: resolveReportText(locale, 'Società Agricola', 'Agricultural company', 'Empresa agrícola'),
    businessName: resolveReportText(locale, 'Azienda Agricola Demo S.r.l.', 'Demo Agricultural Company S.r.l.', 'Demo Compañía Agrícola S.r.l.'),
    officeAddress: resolveReportText(locale, 'Via Roma 123, Roma, 00100, RM, Italia', 'Via Roma 123, Rome, 00100, RM, Italy', 'Via Roma 123, Roma, 00100, RM, Italia'),
    representative: resolveReportText(locale, 'Marco Rossi', 'Marco Rossi', 'Marco Rossi'),
    payingAgency: 'AGEA - Agenzia per le Erogazioni in Agricoltura',
    openingDate: resolveReportText(locale, '01/03/2016', '01/03/2016', '03/01/2016'),
    closingDate: noDataLabel,
    holderType: 'CAA',
    holderCode: 'CAA-IT-0192',
    validationDate: resolveReportText(locale, '15/01/2024', '15/01/2024', '15/01/2024'),
    taxId: '12345678901',
    vatNumber: 'IT12345678901',
    registryNumber: 'RM-2020-11345',
    inpsCode: 'INPS-AGRI-00321',
    bankAccounts: 'IT60X0542811101000000123456',
    laborType: resolveReportText(locale, 'Familiare', 'Family labor', 'Trabajo familiar'),
    laborUnits: '3',
    laborHours: '4200',
    farmArea: '7.1',
  };
  const fascicoloFields = (mappedFascicolo.fields || []).map((field) => {
    const fallback = fascicoloFallbackValues[field.id] || noDataLabel;
    return {
      ...field,
      value: resolveSafeText(field.value, fallback),
      isMissing: false,
    };
  });
  const fascicoloById = new Map(fascicoloFields.map(field => [field.id, field]));
  const groupedFields = (mappedFascicolo.groupedFields || []).map(group => ({
    ...group,
    fields: (group.fields || []).map(field => fascicoloById.get(field.id) || field),
  }));
  const missingFields = [];
  const getFascicoloValue = (fieldId) =>
    fascicoloById.get(fieldId)?.value || fascicoloFallbackValues[fieldId] || null;
  const resolveCoverValue = (value, fallback) => resolveSafeText(value, fallback);
  const resolveCoverOptionalValue = (value, fallback) => resolveSafeText(value, fallback);
  const coverAllRows = [
    {
      label: t('reports.review.template.cover.fields.region'),
      value: resolveCoverValue(dossier?.officeAddress?.region, reportDefaults.region),
    },
    {
      label: t('reports.review.template.cover.fields.province'),
      value: resolveCoverValue(dossier?.officeAddress?.province, reportDefaults.province),
    },
    {
      label: t('reports.review.template.cover.fields.comune'),
      value: resolveCoverValue(dossier?.officeAddress?.city, reportDefaults.comune),
    },
    {
      label: t('reports.review.template.cover.fields.projectName'),
      value: resolveCoverValue(reportTitle, reportTitle),
    },
    {
      label: t('reports.review.template.cover.fields.plantId'),
      value: resolveCoverValue(report?.id, reportIdValue),
    },
    {
      label: t('reports.review.template.cover.fields.power'),
      value: resolveCoverValue(report?.power, reportDefaults.power),
    },
    {
      label: t('reports.review.template.cover.fields.storage'),
      value: resolveCoverValue(report?.storage, reportDefaults.storage),
    },
    {
      label: t('reports.review.template.cover.fields.gridConnection'),
      value: resolveCoverValue(report?.gridConnection, reportDefaults.gridConnection),
    },
    {
      label: t('reports.review.template.cover.fields.documentCode'),
      value: resolveCoverValue(report?.documentCode, reportDefaults.documentCode),
    },
    {
      label: t('reports.review.template.cover.fields.revision'),
      value: resolveCoverValue(report?.revision, reportDefaults.revision),
    },
    {
      label: t('reports.review.template.cover.fields.documentDate'),
      value: resolveCoverValue(formattedGeneratedDate, formattedGeneratedDate),
    },
    {
      label: t('reports.review.template.cover.fields.reportType'),
      value: resolveCoverValue(t(schema.titleKey), t(schema.titleKey)),
    },
    {
      label: t('reports.review.template.cover.fields.client'),
      value: resolveCoverValue(getFascicoloValue('businessName'), fascicoloFallbackValues.businessName),
    },
    {
      label: t('reports.review.template.cover.fields.cuaa'),
      value: resolveCoverValue(getFascicoloValue('cuaa'), fascicoloFallbackValues.cuaa),
    },
    {
      label: t('reports.review.template.cover.fields.officeAddress'),
      value: resolveCoverValue(getFascicoloValue('officeAddress'), fascicoloFallbackValues.officeAddress),
    },
    {
      label: t('reports.review.template.cover.fields.designer'),
      value: resolveCoverValue(report?.designer, reportDefaults.designer),
    },
    {
      label: t('reports.review.template.cover.fields.agronomist'),
      value: resolveCoverValue(report?.agronomist, reportDefaults.agronomist),
    },
    {
      label: t('reports.review.template.cover.fields.confidentiality'),
      value: resolveCoverOptionalValue(
        report?.confidentiality,
        reportDefaults.confidentiality
      ),
    },
  ];
  const coverEssentialLabels = new Set([
    t('reports.review.template.cover.fields.projectName'),
    t('reports.review.template.cover.fields.plantId'),
    t('reports.review.template.cover.fields.province'),
    t('reports.review.template.cover.fields.comune'),
  ]);
  const coverEssentialRows = coverAllRows.filter(row => coverEssentialLabels.has(row.label));
  const coverExtendedRows = coverAllRows.filter(row => !coverEssentialLabels.has(row.label));
  const coverFields = coverAllRows;
  const metadataSectionTitle = resolveReportText(locale, 'Dati documento e impianto', 'Document and project metadata', 'Metadatos de documentos y proyectos.');
  const metadataColumns = [
    { key: 'label', label: resolveReportText(locale, 'Voce', 'Field', 'Campo') },
    { key: 'value', label: resolveReportText(locale, 'Valore', 'Value', 'Valor') },
  ];
  const metadataRows = coverExtendedRows.map(row => ({
    label: row.label,
    value: row.value,
  }));
  const coverBackgroundUrl = getCoverBackgroundUrl(resolvedType, locale);

  const gapItems = [...missingFields];

  const parcelsTable = buildParcelsTable({ landInventory: dossier?.landInventory, t, locale });

  const cropSummaryTable = buildCropSummaryTable({
    landInventory: dossier?.landInventory,
    t,
    locale,
  });
  const buildingsTable = buildBuildingsTable({ buildings: dossier?.buildings, t, locale });
  const equipmentTable = buildEquipmentTable({ means: dossier?.means, t, locale });
  const livestockTable = buildLivestockTable({ livestock: dossier?.livestock, t });

  const coverageTable = buildStationCoverageTable({
    stations: effectiveStations,
    landInventory: dossier?.landInventory,
    t,
    locale,
  });

  const stationInventoryTable = buildStationInventoryTable({ stations: effectiveStations, t });
  const cropNames = (cropSummaryTable?.rows || [])
    .filter(row => row && !row.isTotal && row.crop)
    .map(row => String(row.crop))
    .slice(0, 5);
  const farmAreaValue = (() => {
    const byField = Number(getFascicoloValue('farmArea'));
    if (Number.isFinite(byField) && byField > 0) {
      return byField;
    }
    const fromParcels = (dossier?.landInventory || [])
      .reduce((sum, parcel) => sum + (Number(parcel?.area) || 0), 0);
    return Number.isFinite(fromParcels) && fromParcels > 0 ? fromParcels : null;
  })();
  const sectionNarratives = buildComplianceSectionNarratives({
    locale,
    reportYear,
    reportType: resolvedType,
    businessName: getFascicoloValue('businessName'),
    cuaa: getFascicoloValue('cuaa'),
    farmAreaHa: farmAreaValue,
    stationCount: effectiveStations.length,
    cropNames,
    generatedDate: formattedGeneratedDate,
  });
  const captionLabel = resolveReportText(locale, 'Didascalia', 'Caption', 'Subtítulo');
  const zoneLabelsLabel = resolveReportText(locale, 'Zone', 'Zones', 'Zonas');
  const mapImageLabel = resolveReportText(locale, 'Immagine di riferimento mappa', 'Map reference image', 'Imagen de referencia del mapa');
  const stationMapTitle = resolveReportText(locale, 'Mappa distribuzione stazioni', 'Station distribution map', 'Mapa de distribución de estaciones.');
  const chartTitle = resolveReportText(locale, 'Grafico di monitoraggio', 'Monitoring chart', 'Cuadro de seguimiento');
  const hardwareImagesTitle = resolveReportText(locale, 'Evidenze fotografiche hardware', 'Hardware photo evidence', 'Prueba fotográfica de hardware');
  const retroImagesTitle = resolveReportText(locale, 'Immagini retro modulo', 'Rear module images', 'Imágenes del módulo trasero');
  const campoImagesTitle = resolveReportText(locale, 'Immagini campo aperto', 'Open field images', 'Imágenes de campo abierto');
  const sensorSectionLabel = resolveReportText(locale, 'Sezione sensore', 'Sensor section', 'Sección de sensores');
  const tableCaptionFallback = resolveReportText(locale, 'Tabella generata dai registri operativi disponibili e dai dati di campo validati.', 'Table generated from available operational records and validated field data.', 'Tabla generada a partir de registros operativos disponibles y datos de campo validados.');
  const imageCaptionFallback = resolveReportText(locale, 'Evidenza fotografica raccolta durante le attività di monitoraggio e verifica in campo.', 'Photographic evidence collected during monitoring and field verification activities.', 'Evidencia fotográfica recolectada durante las actividades de monitoreo y verificación de campo.');
  const mapCaptionFallback = resolveReportText(locale, 'Mappa della distribuzione delle stazioni nelle zone monitorate e nelle sezioni operative.', 'Map of station distribution across monitored zones and operative sections.', 'Mapa de distribución de estaciones en zonas monitoreadas y tramos operativos.');
  const chartCaptionFallback = resolveReportText(locale, 'Grafico costruito su misure validate delle stazioni nel periodo di riferimento.', 'Chart built from validated station measurements for the reference period.', 'Gráfico construido a partir de mediciones de estaciones validadas para el período de referencia.');
  const coverageStrategyFallback = resolveReportText(locale, 'La strategia di copertura è basata su posizionamento rappresentativo tra aree retro-modulo e campo aperto.', 'Coverage strategy is based on representative positioning between rear-module and open-field areas.', 'La estrategia de cobertura se basa en un posicionamiento representativo entre el módulo trasero y las áreas de campo abierto.');
  const moduleNarrativeFallback = resolveReportText(locale, 'La sezione consolida le evidenze agronomiche disponibili e i dati operativi per la rendicontazione tecnica.', 'The section consolidates available agronomic evidence and operational data for formal annual reporting.', 'La sección consolida la evidencia agronómica y los datos operativos disponibles para la presentación de informes anuales formales.');
  const sensorInterpretationFallback = resolveReportText(locale, 'Le letture dei sensori sono interpretate rispetto alle operazioni agronomiche, ai trend stagionali e ai riscontri di campo.', 'Sensor readings are interpreted in relation to agronomic operations, seasonal trends, and field observations.', 'Las lecturas de los sensores se interpretan en relación con operaciones agronómicas, tendencias estacionales y observaciones de campo.');

  const getModuleNarrativeFallback = (moduleId) => {
    if (moduleId === 'm0' || (moduleId === 'm1' && isFirstYearReport)) {
      return sectionNarratives['plan-monitoring'] || moduleNarrativeFallback;
    }
    if (moduleId === 'm1' && isAnnualReport) {
      return sectionNarratives['costs-plv'] || moduleNarrativeFallback;
    }
    if (moduleId === 'm2') {
      return sectionNarratives['plan-soil'] || moduleNarrativeFallback;
    }
    if (moduleId === 'm3') {
      return sectionNarratives['plan-irrigation'] || moduleNarrativeFallback;
    }
    if (moduleId === 'm4') {
      return sectionNarratives['plan-microclimate'] || moduleNarrativeFallback;
    }
    return moduleNarrativeFallback;
  };

  const reportModules = getReportModulesByType(resolvedType);
  const moduleContentById = {};

  reportModules.forEach((module) => {
    const moduleName = module.nameKey
      ? t(module.nameKey, { defaultValue: module.fallbackName || module.id })
      : module.fallbackName || module.id;
    const sources = buildModuleSourceLabels(module.id, resolvedType, t);
    const moduleBlocks = getInitialModuleBlocks(resolvedType, module.id, locale) || [];
    const moduleData = getModuleData(module.id, locale);
    const moduleNarrative = (() => {
      if (module.id === 'm0') {
        const textBlocks = moduleBlocks.filter(block => block.type === 'text');
        return buildNarrativeFromTextBlocks(textBlocks);
      }
      if (module.id === 'm1' && resolvedType === 'relazione_agronomica_primo_anno') {
        const strategyBlock = moduleBlocks.find(block => block.id === 'm1-coverage-strategy')
          || moduleBlocks.find(block => block.type === 'text');
        return getBlockText(strategyBlock);
      }
      if (['m2', 'm3', 'm4'].includes(module.id)) {
        return moduleData?.evidence?.description || moduleData?.evidence?.title || '';
      }
      return '';
    })();
    const blocks = [
      {
        type: 'meta',
        label: t('reports.review.template.dataSources.label'),
        value: sources.join(', '),
        isInternal: true,
      },
    ];
    const shouldIncludeModuleNarrative = !(isAnnualReport && module.id === 'm1');
    if (shouldIncludeModuleNarrative) {
      blocks.push({
        type: 'paragraph',
        text: resolveManualText(
          moduleNarrative,
          getModuleNarrativeFallback(module.id)
        ),
      });
    }

    if (module.id === 'm0') {
      const moduleContentBlocks = moduleBlocks
        .filter(block => block.type !== 'text')
        .flatMap((block) => {
          if (block.type === 'table') {
            const table = buildGenericTable({ tableData: block.tableData, t });
            if (!table || !table.columns?.length || !table.rows?.length) {
              return buildFallbackTable({
                title: block.title || t('reports.review.template.tables.sensorInventory'),
                locale,
                captionLabel,
                caption: tableCaptionFallback,
              });
            }
            return {
              type: 'table',
              title: block.title || table.title,
              columns: table.columns,
              rows: table.rows,
              captionLabel,
              caption: resolveCaptionText(
                block.bodyText,
                tableCaptionFallback
              ),
              cellFallback: missingValuePlaceholder,
            };
          }
          if (block.type === 'image') {
            const imageList = normalizeImageList(block.imageList);
            return {
              type: 'image',
              title: block.title || hardwareImagesTitle,
              captionLabel,
              caption: resolveImageCaptionText(
                imageList,
                imageCaptionFallback
              ),
              imageList,
              imageLayout: 'grid',
            };
          }
          return [];
        });

      blocks.push(...moduleContentBlocks);

      const stationTable = stationInventoryTable;
      blocks.push(
        stationTable
          ? {
            type: 'table',
            title: stationTable.title,
            columns: stationTable.columns,
            rows: stationTable.rows,
            captionLabel,
            caption: resolveCaptionText(
              null,
              tableCaptionFallback
            ),
            cellFallback: missingValuePlaceholder,
          }
          : buildFallbackTable({
            title: t('reports.review.template.tables.stationInventory'),
            locale,
            captionLabel,
            caption: tableCaptionFallback,
          })
      );
    }

    if (module.id === 'm1') {
      if (resolvedType === 'relazione_agronomica_annuale') {
        const annualZoneLabels = (coverageTable?.rows || [])
          .filter(row => row && !row.isTotal && row.zone)
          .map(row => row.zone);
        const chartBlocks = moduleBlocks.filter(block => block.type === 'chart');
        if (!chartBlocks.length) {
          blocks.push({
            type: 'map',
            title: stationMapTitle,
            captionLabel,
            caption: mapCaptionFallback,
            zoneLabelLabel: zoneLabelsLabel,
            zoneLabels: annualZoneLabels,
            zonePlaceholder: noDataLabel,
            imageLabel: mapImageLabel,
            imagePlaceholder: noDataLabel,
            imageList: [],
            imageLayout: 'grid',
            chart: {
              variant: 'map',
              showLegend: true,
            },
            fallbackCoverage: COVERAGE_MOCK,
          });
        } else {
          chartBlocks.forEach((chartBlock) => {
            const captionSource = getBlockText(chartBlock) || chartBlock.caption;
            if (chartBlock.chart?.variant === 'map') {
              const imageList = normalizeImageList(chartBlock.imageList);
              const fallbackCoverage = chartBlock.fallbackCoverage || COVERAGE_MOCK;
              const zoneLabels = annualZoneLabels.length
                ? annualZoneLabels
                : (fallbackCoverage?.zones || []).map((zone) => {
                  if (isSpanishLocale(locale)) {
                    return zone?.nameEs || zone?.nameEn || zone?.name;
                  }
                  if (isEnglishLocale(locale)) {
                    return zone?.nameEn || zone?.name;
                  }
                  return zone?.name;
                }).filter(Boolean);
              blocks.push({
                type: 'map',
                title: chartBlock.title || stationMapTitle,
                captionLabel,
                caption: resolveCaptionText(
                  captionSource,
                  chartBlock.bodyPlaceholderKey
                    ? t(chartBlock.bodyPlaceholderKey)
                    : mapCaptionFallback
                ),
                zoneLabelLabel: zoneLabelsLabel,
                zoneLabels,
                zonePlaceholder: noDataLabel,
                imageLabel: mapImageLabel,
                imagePlaceholder: resolveImagePlaceholder(
                  imageList,
                  mapImageLabel
                ),
                imageList,
                imageLayout: 'grid',
                chart: chartBlock.chart || null,
                fallbackCoverage,
              });
              return;
            }
            blocks.push({
              type: 'chart',
              title: chartBlock.title || chartTitle,
              captionLabel,
              caption: resolveCaptionText(
                captionSource,
                chartBlock.bodyPlaceholderKey
                  ? t(chartBlock.bodyPlaceholderKey)
                  : chartCaptionFallback
              ),
              chart: chartBlock.chart || null,
              imageList: normalizeImageList(chartBlock.imageList),
              imageLayout: 'grid',
            });
          });
        }

        const tableBlock = findModuleTableBlock(moduleBlocks);
        if (tableBlock) {
          const table = buildGenericTable({ tableData: tableBlock.tableData, t });
          if (table?.columns?.length && table?.rows?.length) {
            blocks.push({
              type: 'table',
              title: table.title || tableBlock.title || t('reports.modules.m1.productionDataTitle'),
              columns: table.columns,
              rows: table.rows,
              captionLabel,
              caption: resolveCaptionText(
                tableBlock.bodyText,
                tableBlock.bodyPlaceholderKey
                  ? t(tableBlock.bodyPlaceholderKey)
                  : tableCaptionFallback
              ),
              cellFallback: missingValuePlaceholder,
            });
          } else {
            blocks.push(buildFallbackTable({
              title: t('reports.modules.m1.productionDataTitle'),
              locale,
              captionLabel,
              caption: tableCaptionFallback,
            }));
          }
        } else {
          blocks.push(buildFallbackTable({
            title: t('reports.modules.m1.productionDataTitle'),
            locale,
            captionLabel,
            caption: tableCaptionFallback,
          }));
        }

        const narrativeBlocks = moduleBlocks.filter(block => block.type === 'text' && block.blockRole !== 'callout');
        narrativeBlocks.forEach(block => {
          blocks.push(...buildTextParagraphBlocks(
            block,
            getModuleNarrativeFallback(module.id)
          ));
        });

        const calloutBlocks = moduleBlocks.filter(block => block.type === 'text' && block.blockRole === 'callout');
        calloutBlocks.forEach(block => {
          blocks.push(...buildTextParagraphBlocks(
            block,
            getModuleNarrativeFallback(module.id)
          ));
        });
      } else {
        const zoneLabels = (coverageTable?.rows || [])
          .filter(row => row && !row.isTotal && row.zone)
          .map(row => row.zone);
        const moduleContentBlocks = moduleBlocks.flatMap((block) => {
          if (block.type === 'chart' && block.chart?.variant === 'map') {
            const imageList = normalizeImageList(block.imageList);
            const captionSource = getBlockText(block) || block.caption;
            const fallbackCoverage = block.fallbackCoverage || COVERAGE_MOCK;
            return {
              type: 'map',
              title: block.title || stationMapTitle,
              captionLabel,
              caption: resolveCaptionText(
                captionSource,
                block.bodyPlaceholderKey
                  ? t(block.bodyPlaceholderKey)
                  : mapCaptionFallback
              ),
              zoneLabelLabel: zoneLabelsLabel,
              zoneLabels,
              zonePlaceholder: noDataLabel,
              imageLabel: mapImageLabel,
              imagePlaceholder: resolveImagePlaceholder(
                imageList,
                mapImageLabel
              ),
              imageList,
              imageLayout: 'grid',
              chart: block.chart || null,
              fallbackCoverage,
            };
          }
          if (block.type === 'table') {
            const table = block.tableData?.source === 'stationCoverage'
              ? coverageTable
              : buildGenericTable({ tableData: block.tableData, t });
            if (!table || !table.columns?.length || !table.rows?.length) {
              return buildFallbackTable({
                title: t('reports.review.template.tables.coverage'),
                locale,
                captionLabel,
                caption: tableCaptionFallback,
              });
            }
            return {
              type: 'table',
              title: table.title || block.title || t('reports.review.template.tables.coverage'),
              columns: table.columns,
              rows: table.rows,
              captionLabel,
              caption: resolveCaptionText(
                getBlockText(block),
                block.bodyPlaceholderKey
                  ? t(block.bodyPlaceholderKey)
                  : tableCaptionFallback
              ),
              cellFallback: missingValuePlaceholder,
            };
          }
          if (block.type === 'text') {
            const localizedBlock = { ...block, bodyText: getBlockText(block) };
            return buildTextParagraphBlocks(
              localizedBlock,
              coverageStrategyFallback
            );
          }
          return [];
        });

        blocks.push(...moduleContentBlocks);
      }
    }

    let children = [];
    if (['m2', 'm3', 'm4'].includes(module.id)) {
      const sensors = moduleData?.evidence?.sensors || [];
      if (sensors.length === 0) {
        gapItems.push({
          id: `sensors-${module.id}`,
          label: t('reports.review.template.gaps.sensors', { module: moduleName }),
          sectionId: module.id,
          required: true,
        });
      }
      const sensorSections = sensors.map((sensor, index) => {
        const sensorTitle = sensor.name || `${sensorSectionLabel} ${index + 1}`;
        const chartA = sensor.charts?.[0];
        const chartB = sensor.charts?.[1];
        const chartLabel = chartCaptionFallback;
        const imageLabel = imageCaptionFallback;
        const tableLabel = tableCaptionFallback;
        const chartConfig = (chart) => (
          chart
            ? {
              variant: 'line',
              data: chart.data || [],
              xKey: 'label',
              yKey: 'value',
              unit: chart.unit,
            }
            : null
        );
        const retroImages = normalizeImageList(sensor.images?.retroModulo);
        const campoImages = normalizeImageList(sensor.images?.campoAperto);
        const sensorBlocks = [
          {
            type: 'chart',
            title: chartA?.title || chartTitle,
            captionLabel,
            caption: resolveCaptionText(chartA?.note, chartLabel),
            chart: chartConfig(chartA),
          },
          {
            type: 'chart',
            title: chartB?.title || chartTitle,
            captionLabel,
            caption: resolveCaptionText(chartB?.note, chartLabel),
            chart: chartConfig(chartB),
          },
          {
            type: 'image',
            title: retroImagesTitle,
            captionLabel,
            caption: resolveImageCaptionText(retroImages, imageLabel),
            imageList: retroImages,
            imageLayout: 'grid',
          },
          {
            type: 'image',
            title: campoImagesTitle,
            captionLabel,
            caption: resolveImageCaptionText(campoImages, imageLabel),
            imageList: campoImages,
            imageLayout: 'grid',
          },
          {
            type: 'paragraph',
            text: resolveManualText(
              sensor.explanation,
              sensorInterpretationFallback
            ),
          },
        ];
        if (sensor.tableData) {
          const table = buildGenericTable({ tableData: sensor.tableData, t });
          if (table?.columns?.length && table?.rows?.length) {
            sensorBlocks.splice(2, 0, {
              type: 'table',
              title: table.title || t('reports.review.template.tables.supporting'),
              columns: table.columns,
              rows: table.rows,
              captionLabel,
              caption: resolveCaptionText(sensor.tableCaption, tableLabel),
              cellFallback: missingValuePlaceholder,
            });
          }
        }
        return createSection({
          id: `${module.id}-${sensor.id || index}`,
          title: sensorTitle,
          level: 3,
          blocks: sensorBlocks,
        });
      });
      children = sensorSections;
    }

    moduleContentById[module.id] = { blocks, children };
  });

  const buildSectionNarrativeBlocks = (sectionId, sectionTitle) => {
    const resolvedText = resolveManualText(
      sectionNarratives[sectionId],
      resolveReportText(
        locale,
        `Questa sezione fornisce analisi agronomica formale per \"${sectionTitle}\" sulla base delle evidenze disponibili.`,
        `This section provides formal agronomic analysis for \"${sectionTitle}\" based on available evidence.`,
        `Esta sección proporciona un análisis agronómico formal para \"${sectionTitle}\" basado en la evidencia disponible.`,
      )
    );
    return splitNarrativeParagraphs(resolvedText)
      .map(text => ({
        type: 'paragraph',
        text,
      }));
  };

  const buildTableBlock = (table, fallbackLabel) => {
    if (table?.columns?.length && table?.rows?.length) {
      return {
        type: 'table',
        title: table.title || fallbackLabel,
        columns: table.columns,
        rows: table.rows,
        captionLabel,
        caption: resolveCaptionText(
          null,
          tableCaptionFallback
        ),
        cellFallback: missingValuePlaceholder,
      };
    }
    return buildFallbackTable({
      title: fallbackLabel,
      locale,
      captionLabel,
      caption: tableCaptionFallback,
    });
  };

  const parcelsBlock = buildTableBlock(
    parcelsTable,
    t('reports.review.template.tables.parcels')
  );
  const cropSummaryBlock = buildTableBlock(
    cropSummaryTable,
    t('reports.review.template.tables.cropSummary')
  );
  const buildingsBlock = buildTableBlock(
    buildingsTable,
    t('reports.review.template.tables.buildings')
  );
  const equipmentBlock = buildTableBlock(
    equipmentTable,
    t('reports.review.template.tables.means')
  );
  const livestockBlock = livestockTable
    ? buildTableBlock(livestockTable, t('reports.review.template.tables.livestock'))
    : null;

  const fascicoloFieldSections = groupedFields.map((group) => createSection({
    id: `fascicolo-${group.id}`,
    title: group.title,
    level: 2,
    blocks: [
      {
        type: 'fieldList',
        items: group.fields.map((field) => ({
          label: field.label,
          value: field.value,
        })),
      },
    ],
  }));

  const environmentalTitle = t('reports.review.template.sections.environmentalAnalysis');
  const environmentalChildren = [
    ...fascicoloFieldSections,
    createSection({
      id: 'fascicolo-buildings',
      title: t('reports.review.template.sections.buildings'),
      level: 2,
      blocks: [buildingsBlock],
    }),
    livestockBlock
      ? createSection({
        id: 'fascicolo-livestock',
        title: t('reports.review.template.sections.livestock'),
        level: 2,
        blocks: [livestockBlock],
      })
      : null,
  ].filter(Boolean);

  const planTitle = t('reports.review.template.sections.plan');
  const cultivationOrganizationTitle = t('reports.review.template.sections.cultivationOrganization');
  const cultivationSurfacesTitle = t('reports.review.template.sections.cultivationSurfaces');
  const cropDescriptionTitle = t('reports.review.template.sections.cropDescription');
  const monitoringTitle = t('reports.review.template.sections.monitoringPrecision');
  const irrigationTitle = t('reports.review.template.sections.irrigationSavings');
  const soilMonitoringTitle = t('reports.review.template.sections.soilMonitoring');
  const microclimateTitle = t('reports.review.template.sections.microclimate');
  const cropRotationTitle = t('reports.review.template.sections.cropRotation');

  const monitoringBlocks = [
    ...buildSectionNarrativeBlocks('plan-monitoring', monitoringTitle),
    ...(isFirstYearReport ? (moduleContentById.m0?.blocks || []) : []),
    ...(isFirstYearReport ? (moduleContentById.m1?.blocks || []) : []),
  ];
  const monitoringChildren = [
    ...(isFirstYearReport ? (moduleContentById.m0?.children || []) : []),
    ...(isFirstYearReport ? (moduleContentById.m1?.children || []) : []),
  ];

  const irrigationBlocks = [
    ...buildSectionNarrativeBlocks('plan-irrigation', irrigationTitle),
    ...(moduleContentById.m3?.blocks || []),
  ];
  const irrigationChildren = moduleContentById.m3?.children || [];

  const soilBlocks = [
    ...buildSectionNarrativeBlocks('plan-soil', soilMonitoringTitle),
    ...(moduleContentById.m2?.blocks || []),
  ];
  const soilChildren = moduleContentById.m2?.children || [];

  const microclimateBlocks = [
    ...buildSectionNarrativeBlocks('plan-microclimate', microclimateTitle),
    ...(moduleContentById.m4?.blocks || []),
  ];
  const microclimateChildren = moduleContentById.m4?.children || [];

  const premessaTitle = t('reports.review.template.sections.premessa');
  const objectivesTitle = t('reports.review.template.sections.objectives');
  const mechanizationTitle = t('reports.review.template.sections.mechanization');
  const cropSuccessionTitle = t('reports.review.template.sections.cropSuccession');
  const implementationTitle = t('reports.review.template.sections.implementationAnalysis');
  const constructionTitle = t('reports.review.template.sections.constructionCompatibility');
  const plantLayoutTitle = t('reports.review.template.sections.plantLayout');
  const humanResourcesTitle = t('reports.review.template.sections.humanResourcesCompatibility');
  const integratedTitle = t('reports.review.template.sections.integratedProject');
  const integratedEnvironmentalTitle = t('reports.review.template.sections.integratedEnvironmental');
  const integratedSocialTitle = t('reports.review.template.sections.integratedSocial');
  const integratedTechnicalTitle = t('reports.review.template.sections.integratedTechnical');
  const costsTitle = t('reports.review.template.sections.costsPlv');
  const guidelinesTitle = t('reports.review.template.sections.guidelinesCompliance');

  const costsBlocks = [
    ...buildSectionNarrativeBlocks('costs-plv', costsTitle),
    ...(isAnnualReport ? (moduleContentById.m1?.blocks || []) : []),
  ];

  const outlineSectionsById = {
    premessa: createSection({
      id: 'premessa',
      title: premessaTitle,
      level: 1,
      blocks: buildSectionNarrativeBlocks('premessa', premessaTitle),
    }),
    objectives: createSection({
      id: 'objectives',
      title: objectivesTitle,
      level: 1,
      blocks: buildSectionNarrativeBlocks('objectives', objectivesTitle),
    }),
    'environmental-analysis': createSection({
      id: 'environmental-analysis',
      title: environmentalTitle,
      level: 1,
      blocks: buildSectionNarrativeBlocks('environmental-analysis', environmentalTitle),
      children: environmentalChildren,
    }),
    'piano-colturale': createSection({
      id: 'piano-colturale',
      title: planTitle,
      level: 1,
      blocks: buildSectionNarrativeBlocks('piano-colturale', planTitle),
      children: [
        createSection({
          id: 'plan-organization',
          title: cultivationOrganizationTitle,
          level: 2,
          blocks: buildSectionNarrativeBlocks('plan-organization', cultivationOrganizationTitle),
        }),
        createSection({
          id: 'plan-surfaces',
          title: cultivationSurfacesTitle,
          level: 2,
          blocks: [...buildSectionNarrativeBlocks('plan-surfaces', cultivationSurfacesTitle), parcelsBlock],
        }),
        createSection({
          id: 'plan-crops',
          title: cropDescriptionTitle,
          level: 2,
          blocks: [...buildSectionNarrativeBlocks('plan-crops', cropDescriptionTitle), cropSummaryBlock],
        }),
        createSection({
          id: 'plan-monitoring',
          title: monitoringTitle,
          level: 2,
          blocks: monitoringBlocks,
          children: monitoringChildren,
        }),
        createSection({
          id: 'plan-irrigation',
          title: irrigationTitle,
          level: 2,
          blocks: irrigationBlocks,
          children: irrigationChildren,
        }),
        createSection({
          id: 'plan-soil',
          title: soilMonitoringTitle,
          level: 2,
          blocks: soilBlocks,
          children: soilChildren,
        }),
        createSection({
          id: 'plan-microclimate',
          title: microclimateTitle,
          level: 2,
          blocks: microclimateBlocks,
          children: microclimateChildren,
        }),
        createSection({
          id: 'plan-rotation',
          title: cropRotationTitle,
          level: 2,
          blocks: buildSectionNarrativeBlocks('plan-rotation', cropRotationTitle),
        }),
      ],
    }),
    mechanization: createSection({
      id: 'mechanization',
      title: mechanizationTitle,
      level: 1,
      blocks: [...buildSectionNarrativeBlocks('mechanization', mechanizationTitle), equipmentBlock],
    }),
    'crop-succession': createSection({
      id: 'crop-succession',
      title: cropSuccessionTitle,
      level: 1,
      blocks: buildSectionNarrativeBlocks('crop-succession', cropSuccessionTitle),
    }),
    'implementation-analysis': createSection({
      id: 'implementation-analysis',
      title: implementationTitle,
      level: 1,
      blocks: buildSectionNarrativeBlocks('implementation-analysis', implementationTitle),
    }),
    'construction-compatibility': createSection({
      id: 'construction-compatibility',
      title: constructionTitle,
      level: 1,
      blocks: buildSectionNarrativeBlocks('construction-compatibility', constructionTitle),
      children: [
        createSection({
          id: 'construction-layout',
          title: plantLayoutTitle,
          level: 2,
          blocks: buildSectionNarrativeBlocks('construction-layout', plantLayoutTitle),
        }),
        createSection({
          id: 'construction-resources',
          title: humanResourcesTitle,
          level: 2,
          blocks: buildSectionNarrativeBlocks('construction-resources', humanResourcesTitle),
        }),
      ],
    }),
    'integrated-project': createSection({
      id: 'integrated-project',
      title: integratedTitle,
      level: 1,
      blocks: buildSectionNarrativeBlocks('integrated-project', integratedTitle),
      children: [
        createSection({
          id: 'integrated-environmental',
          title: integratedEnvironmentalTitle,
          level: 2,
          blocks: buildSectionNarrativeBlocks('integrated-environmental', integratedEnvironmentalTitle),
        }),
        createSection({
          id: 'integrated-social',
          title: integratedSocialTitle,
          level: 2,
          blocks: buildSectionNarrativeBlocks('integrated-social', integratedSocialTitle),
        }),
        createSection({
          id: 'integrated-technical',
          title: integratedTechnicalTitle,
          level: 2,
          blocks: buildSectionNarrativeBlocks('integrated-technical', integratedTechnicalTitle),
        }),
      ],
    }),
    'costs-plv': createSection({
      id: 'costs-plv',
      title: costsTitle,
      level: 1,
      blocks: costsBlocks,
      children: isAnnualReport ? (moduleContentById.m1?.children || []) : [],
    }),
    'guidelines-compliance': createSection({
      id: 'guidelines-compliance',
      title: guidelinesTitle,
      level: 1,
      blocks: buildSectionNarrativeBlocks('guidelines-compliance', guidelinesTitle),
    }),
  };

  const signatureBlock = {
    type: 'fieldList',
    items: [
      {
        label: t('reports.review.template.signature.locationDate'),
        value: `${reportDefaults.comune}, ${formattedGeneratedDate}`,
      },
      {
        label: t('reports.review.template.signature.agronomist'),
        value: resolveSafeText(report?.agronomist, reportDefaults.agronomist),
      },
      {
        label: t('reports.review.template.signature.registry'),
        value: resolveReportText(locale, 'Ordine dei Dottori Agronomi di Roma, n. 458', 'Order of Agronomists - Rome, n. 458', 'Orden de los Agrónomos - Roma, n. 458'),
      },
      {
        label: t('reports.review.template.signature.stamp'),
        value: resolveReportText(locale, 'Firma digitale allegata', 'Digital signature attached', 'Firma digital adjunta'),
      },
    ],
  };

  const coverLines = [
    t('reports.review.template.cover.reportTitle'),
    reportTitle,
    t('reports.review.template.cover.referenceYear', { year: reportYear }),
  ];
  const coverMetadataSection = createSection({
    id: 'cover-metadata',
    title: metadataSectionTitle,
    level: 1,
    includeInToc: false,
    blocks: [
      {
        type: 'table',
        title: '',
        columns: metadataColumns,
        rows: metadataRows,
        captionLabel: '',
        caption: '',
        cellFallback: missingValuePlaceholder,
      },
    ],
  });

  const sections = schema.sections.reduce((acc, section) => {
    if (section.type === 'cover') {
      acc.push(createSection({
        id: 'cover',
        title: '',
        level: 1,
        includeInToc: false,
        blocks: [
          {
            type: 'cover',
            lines: coverLines,
            title: coverLines[0],
            subtitle: coverLines[1],
            referenceLine: coverLines[2],
            client: getFascicoloValue('businessName'),
            date: formattedGeneratedDate,
            backgroundUrl: coverBackgroundUrl,
            metadataRows: coverEssentialRows,
            essentialRows: coverEssentialRows,
            extendedRows: coverExtendedRows,
            metadataSectionTitle,
          },
        ],
      }));
      return acc;
    }
    if (section.type === 'toc') {
      acc.push(createSection({
        id: 'toc',
        title: t('reports.review.template.sections.toc'),
        level: 1,
        includeInToc: false,
        blocks: [
          {
            type: 'toc',
            items: [],
          },
        ],
      }));
      acc.push(coverMetadataSection);
      return acc;
    }
    if (section.type === 'gapReport') {
      acc.push(createSection({
        id: 'data-gaps',
        title: t('reports.review.template.sections.dataGaps'),
        level: 1,
        includeInToc: false,
        isInternal: true,
        blocks: [
          {
            type: 'gapReport',
            items: gapItems,
            emptyMessage: t('reports.review.gapReport.empty'),
          },
        ],
      }));
      return acc;
    }
    if (section.type === 'outline') {
      acc.push(
        outlineSectionsById[section.id]
        || createSection({
          id: section.id,
          title: section.titleKey ? t(section.titleKey) : section.title,
          level: section.level || 1,
        })
      );
      return acc;
    }
    if (section.type === 'conclusions') {
      const conclusionNarrativeBlocks = splitNarrativeParagraphs(
        resolveManualText(sectionNarratives.conclusions, sectionNarratives['conclusions-attestation'])
      ).map(text => ({
        type: 'paragraph',
        text,
      }));
      acc.push(createSection({
        id: section.id,
        title: t(section.titleKey),
        level: 1,
        blocks: [
          ...conclusionNarrativeBlocks,
          {
            type: 'paragraph',
            text: sectionNarratives['conclusions-attestation'],
          },
          signatureBlock,
        ],
      }));
      return acc;
    }
    if (section.type === 'metadata') {
      acc.push(createSection({
        id: section.id,
        title: t(section.titleKey),
        level: 1,
        includeInToc: false,
        isInternal: true,
        blocks: [
          {
            type: 'fieldList',
            items: [
              {
                label: t('reports.review.template.metadata.version'),
                value: REPORT_TEMPLATE_SCHEMA_VERSION,
              },
              {
                label: t('reports.review.template.metadata.generatedAt'),
                value: generatedAt,
              },
              {
                label: t('reports.review.template.metadata.reportId'),
                value: report?.id || t('common.notAvailable'),
              },
              {
                label: t('reports.review.template.metadata.reportType'),
                value: t(schema.titleKey),
              },
              {
                label: t('reports.review.template.metadata.language'),
                value: locale.toUpperCase(),
              },
              {
                label: t('reports.review.template.metadata.dataSources'),
                value: [
                  t(REPORT_TEMPLATE_DATA_SOURCES.fascicolo.labelKey),
                  t(REPORT_TEMPLATE_DATA_SOURCES.stationInventory.labelKey),
                  t(REPORT_TEMPLATE_DATA_SOURCES.userInput.labelKey),
                ].join(', '),
              },
            ],
          },
        ],
      }));
      return acc;
    }
    acc.push(createSection(section));
    return acc;
  }, []);

  const exportSections = filterExportSections(sections);
  const flatSections = flattenSections(exportSections);
  const tocItems = buildTocItems(flatSections);
  const tocSection = sections.find(sec => sec.id === 'toc');
  if (tocSection && tocSection.blocks?.length) {
    tocSection.blocks[0].items = tocItems;
  }

  const textLines = [];
  exportSections.forEach((section) => {
    const serialized = serializeSection(section, missingValuePlaceholder);
    if (serialized.length > 0) {
      textLines.push(...serialized, '');
    }
  });
  const coverSection = sections.find(sec => sec.id === 'cover');
  const resolvedCoverLines = coverSection?.blocks?.find(block => block.type === 'cover')?.lines || coverLines;
  const tocTitle = sections.find(sec => sec.id === 'toc')?.title || t('reports.review.template.sections.toc');
  const latex = buildLatexDocument({
    sections: exportSections,
    placeholder: missingValuePlaceholder,
    coverLines: resolvedCoverLines,
    coverFields,
    reportTypeLabel: t(schema.titleKey),
    reportTitleLabel: t('reports.review.template.cover.reportTitle'),
    projectTitle: reportTitle,
    locale,
    tocTitle,
  });

  const document = {
    version: REPORT_TEMPLATE_SCHEMA_VERSION,
    reportType: schema.id,
    locale,
    toc: tocItems,
    sections,
    exportSections,
    gaps: gapItems,
  };

  return {
    document,
    toc: tocItems,
    gapReport: {
      items: gapItems,
    },
    text: textLines.join('\n').trim(),
    json: JSON.stringify(document, null, 2),
    latex,
  };
};
